import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  BarChart3,
  ChevronRight,
  Clock,
  DollarSign,
  FileSpreadsheet,
  Percent,
  Printer,
  ShieldCheck,
  Table as TableIcon,
  TrendingUp
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { AnnualTimelineRow, AskWrennenContext, DebtEvent, DebtRecord, FinancialInputs, HistoricalActualYear, MonthlyProjectionRow, Portfolio, Property, PropertyAuditRecord, PropertyDebtLink, PropertyModelEvent, PropertyRiskProfile, SavedModelScenario, Team, TeamMember, TeamRole, YearData } from './types';
import { buildModel } from './services/financials';
import { computeFinancialSnapshotScores, computeRiskSummary, createDefaultRiskProfile, normalizeRiskProfile } from './services/risk';
import { formatCurrency0, formatCurrency2, formatNumber2, formatPercent2 } from './utils/format';
import { usePropertyStore } from './store/propertyStore';
import SummaryCard from './components/SummaryCard';
import FinancialSnapshot from './components/FinancialSnapshot';
import Sidebar from './components/Sidebar';
import PortfolioView from './components/PortfolioView';
import LandingPage from './components/LandingPage';
import RiskAnalysisView from './components/RiskAnalysisView';
import BasePropertyForm from './components/BasePropertyForm';
import AuthScreen from './components/AuthScreen';
import PropertyDashboardHome from './components/PropertyDashboardHome';
import { useAuth } from './contexts/AuthContext';
import DebtView from './components/DebtView';
import TaxReadinessView from './components/TaxReadinessView';
import { buildDebtSnapshotForProperty, normalizeDebtRecord, resolveDebtAllocationPercent } from './services/debt';
import AskWrennenDrawer from './components/AskWrennenDrawer';
import { supabase } from './lib/supabaseClient';

const createInitialInputs = (): FinancialInputs => ({
  purchasePrice: 750000,
  downPayment: 300000,
  acquisitionCosts: 0,
  lenderFees: 0,
  initialCapex: 0,
  interestRate: 6.5,
  amortYears: 20,
  annualRent: 107400,
  vacancyRate: 5,
  annualTaxes: 7800,
  annualInsurance: 2400,
  annualMaintenance: 5400,
  annualHoa: 0,
  propertyManagementRate: 8,
  rentGrowth: 3,
  expenseGrowth: 3,
  annualCapexReserve: 0,
  reserveFundingMode: 'off',
  reservePercentOfRent: 0,
  otherIncomeMonthly: 0,
  utilitiesMonthly: 0,
  holdPeriod: 30,
  projectionMonths: 24,
  vacancyMode: 'smoothed',
  exitCapRate: 0,
  saleCosts: 5,
  exitMethod: 'cap',
  appreciationRate: 4,
  propertyReserves: 0,
  portfolioReservesAllocated: 0,
  includeReserveReleaseOnSale: false,
  landAllocationPercent: 20,
  buildingAllocationPercent: 80,
  capitalImprovements: 0,
  placedInServiceDate: '',
  depreciationMethod: 'residential_rental_sl_27_5',
  reserveInputsVersion: 1,
  investmentInputsVersion: 1
});

const CHART_RANGES = [24, 36, 60] as const;
const INITIAL_BASE_YEAR = new Date().getFullYear();
type PropertyViewMode = 'create-core' | 'create-operations' | 'create-forecast' | 'edit-core' | 'edit-operations' | 'edit-forecast' | 'dashboard';
type DashboardDetailView = 'simple' | 'advanced';
type ThemeMode = 'light' | 'dark';
type PropertySyncStatus = {
  tone: 'success' | 'error' | 'info';
  message: string;
} | null;
type SupabasePropertyRow = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
  team_id: string | null;
  display_order: number | null;
  name: string | null;
  location: string | null;
  ownership_entity: string | null;
  base_year: number | null;
  purchase_price: number | null;
  equity_contribution: number | null;
  annual_rent: number | null;
  annual_taxes: number | null;
  annual_insurance: number | null;
  annual_maintenance: number | null;
  vacancy_rate: number | null;
  rent_growth_rate: number | null;
  expense_growth_rate: number | null;
  appreciation_rate: number | null;
  exit_cap_rate: number | null;
  exit_costs: number | null;
  hold_period: number | null;
  current_year_inputs: FinancialInputs | null;
  historical_actuals: HistoricalActualYear[] | null;
  risk_profile: Partial<PropertyRiskProfile> | null;
  model_events: PropertyModelEvent[] | null;
  saved_scenarios: SavedModelScenario[] | null;
  audit_log: PropertyAuditRecord[] | null;
};
type SupabasePropertyDebtRow = {
  id: string;
  created_at: string | null;
  user_id: string;
  team_id: string | null;
  property_id: string;
  ownership_entity: string | null;
  is_shared_portfolio_debt: boolean | null;
  loan_name: string | null;
  loan_type: string | null;
  lender: string | null;
  loan_amount: number | null;
  current_balance: number | null;
  interest_rate: number | null;
  amort_years: number | null;
  loan_term_years: number | null;
  monthly_payment: number | null;
  start_date: string | null;
  first_payment_date: string | null;
  payment_day_of_month: number | null;
  maturity_date: string | null;
  interest_only: boolean | null;
  interest_only_months: number | null;
  balloon_date: string | null;
  balloon_amount: number | null;
  credit_limit: number | null;
  assume_scheduled_payments_made: boolean | null;
  notes: string | null;
};
type SupabasePropertyDebtLinkRow = {
  id: string;
  created_at: string | null;
  user_id: string;
  team_id: string | null;
  debt_id: string;
  property_id: string;
  allocation_percent: number | null;
  allocation_method: string | null;
  value_basis: string | null;
  manual_value_override: number | null;
};
type SupabaseDebtEventRow = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
  team_id: string | null;
  debt_id: string;
  event_date: string | null;
  event_type: string | null;
  amount: number | null;
  notes: string | null;
};
type SupabasePortfolioRow = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
  team_id: string | null;
  name: string | null;
};
type SupabasePortfolioPropertyRow = {
  id: string;
  created_at: string | null;
  user_id: string;
  team_id: string | null;
  portfolio_id: string;
  property_id: string;
};
type SupabaseTeamRow = {
  id: string;
  created_at: string | null;
  owner_user_id: string | null;
  name: string | null;
  is_personal: boolean | null;
};
type SupabaseTeamMembershipRow = {
  id: string;
  created_at: string | null;
  team_id: string;
  user_id: string;
  role: TeamRole | null;
};
type SupabaseUserProfileRow = {
  id: string;
  email: string | null;
  display_name: string | null;
};
type SupabasePropertyPayload = {
  display_order: number;
  name: string;
  location: string | null;
  ownership_entity: string | null;
  base_year: number;
  purchase_price: number;
  equity_contribution: number;
  annual_rent: number;
  annual_taxes: number;
  annual_insurance: number;
  annual_maintenance: number;
  vacancy_rate: number;
  rent_growth_rate: number;
  expense_growth_rate: number;
  appreciation_rate: number;
  exit_cap_rate: number;
  exit_costs: number;
  hold_period: number;
  current_year_inputs: FinancialInputs;
  historical_actuals: HistoricalActualYear[];
  risk_profile: PropertyRiskProfile;
  model_events: PropertyModelEvent[];
  saved_scenarios: SavedModelScenario[];
  audit_log: PropertyAuditRecord[];
};
const SUPABASE_PROPERTY_COLUMNS = [
  'id',
  'created_at',
  'updated_at',
  'user_id',
  'team_id',
  'display_order',
  'name',
  'location',
  'ownership_entity',
  'base_year',
  'purchase_price',
  'equity_contribution',
  'annual_rent',
  'annual_taxes',
  'annual_insurance',
  'annual_maintenance',
  'vacancy_rate',
  'rent_growth_rate',
  'expense_growth_rate',
  'appreciation_rate',
  'exit_cap_rate',
  'exit_costs',
  'hold_period',
  'current_year_inputs',
  'historical_actuals',
  'risk_profile',
  'model_events',
  'saved_scenarios',
  'audit_log'
].join(', ');
const SUPABASE_PROPERTY_SUMMARY_COLUMNS = [
  'id',
  'created_at',
  'updated_at',
  'team_id',
  'display_order',
  'name',
  'location',
  'ownership_entity',
  'base_year',
  'purchase_price',
  'equity_contribution',
  'annual_rent'
].join(', ');
const SUPABASE_PROPERTY_DEBT_MUTATION_COLUMNS = [
  'property_id',
  'ownership_entity',
  'is_shared_portfolio_debt',
  'loan_name',
  'loan_type',
  'lender',
  'loan_amount',
  'current_balance',
  'interest_rate',
  'amort_years',
  'loan_term_years',
  'monthly_payment',
  'start_date',
  'first_payment_date',
  'payment_day_of_month',
  'maturity_date',
  'interest_only',
  'interest_only_months',
  'balloon_date',
  'balloon_amount',
  'credit_limit',
  'assume_scheduled_payments_made',
  'notes'
] as const;
const SUPABASE_PROPERTY_DEBT_COLUMNS = [
  'id',
  'created_at',
  'user_id',
  'team_id',
  ...SUPABASE_PROPERTY_DEBT_MUTATION_COLUMNS
].join(', ');
const SUPABASE_PROPERTY_DEBT_LINK_COLUMNS = 'id, created_at, user_id, team_id, debt_id, property_id, allocation_percent, allocation_method, value_basis, manual_value_override';
const SUPABASE_DEBT_EVENT_COLUMNS = 'id, created_at, updated_at, user_id, team_id, debt_id, event_date, event_type, amount, notes';
const SUPABASE_PORTFOLIO_COLUMNS = 'id, created_at, updated_at, user_id, team_id, name';
const SUPABASE_PORTFOLIO_PROPERTY_COLUMNS = 'id, created_at, user_id, team_id, portfolio_id, property_id';
const SUPABASE_TEAM_COLUMNS = 'id, created_at, owner_user_id, name, is_personal';
const SUPABASE_TEAM_MEMBERSHIP_COLUMNS = 'id, created_at, team_id, user_id, role';
const SUPABASE_USER_PROFILE_COLUMNS = 'id, email, display_name';
const ACTIVE_TEAM_STORAGE_KEY = 'wrennen_active_team_id';
const THEME_MODE_STORAGE_KEY = 'wrennen_theme_mode';

const normalizeSupabaseNumber = (value: unknown, fallback = 0) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeSupabaseInteger = (value: unknown, fallback = 1) => (
  Math.max(1, Math.round(normalizeSupabaseNumber(value, fallback)))
);
const normalizeNullableIsoDate = (value?: string | null) => value || null;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (value?: string | null) => Boolean(value && UUID_PATTERN.test(value));
const describeSupabaseError = (error: any) => error ? {
  message: error.message || 'Unknown Supabase error',
  code: error.code ?? null,
  details: error.details ?? null,
  hint: error.hint ?? null
} : null;
const yieldToBrowser = () => new Promise<void>((resolve) => {
  window.setTimeout(resolve, 0);
});
const asObject = (value: unknown, fallback: Record<string, unknown>) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fallback
);
const asArray = (value: unknown) => (
  Array.isArray(value) ? value : null
);
const stripDerivedScenarioFields = (savedScenarios: SavedModelScenario[]) => (
  savedScenarios.map((scenario) => ({
    ...scenario,
    summary: null,
    lastRunAt: null
  }))
);
const buildPropertyBaselineSignature = (config: {
  name: string;
  address: string;
  baseYear: number;
  inputs: FinancialInputs;
  historicalActuals: HistoricalActualYear[];
  riskProfile: PropertyRiskProfile;
}) => JSON.stringify({
  name: config.name,
  address: config.address,
  baseYear: config.baseYear,
  inputs: config.inputs,
  historicalActuals: config.historicalActuals,
  riskProfile: config.riskProfile
});

const createPersistedPropertyInputs = (): FinancialInputs => ({
  purchasePrice: 0,
  downPayment: 0,
  acquisitionCosts: 0,
  lenderFees: 0,
  initialCapex: 0,
  interestRate: 0,
  amortYears: 30,
  annualRent: 0,
  vacancyRate: 0,
  annualTaxes: 0,
  annualInsurance: 0,
  annualMaintenance: 0,
  annualHoa: 0,
  propertyManagementRate: 0,
  rentGrowth: 0,
  expenseGrowth: 0,
  annualCapexReserve: 0,
  reserveFundingMode: 'off',
  reservePercentOfRent: 0,
  otherIncomeMonthly: 0,
  utilitiesMonthly: 0,
  holdPeriod: 30,
  projectionMonths: 24,
  vacancyMode: 'smoothed',
  exitCapRate: 0,
  saleCosts: 0,
  exitMethod: 'cap',
  appreciationRate: 0,
  propertyReserves: 0,
  portfolioReservesAllocated: 0,
  includeReserveReleaseOnSale: false,
  landAllocationPercent: 20,
  buildingAllocationPercent: 80,
  capitalImprovements: 0,
  placedInServiceDate: '',
  depreciationMethod: 'residential_rental_sl_27_5',
  reserveInputsVersion: 1,
  investmentInputsVersion: 1
});

const buildSupabasePropertyPayload = (
  displayOrder: number,
  name: string,
  address: string,
  ownershipEntity: string,
  baseYear: number,
  propertyInputs: FinancialInputs,
  historicalActuals: HistoricalActualYear[],
  riskProfile: PropertyRiskProfile,
  modelEvents: PropertyModelEvent[],
  savedScenarios: SavedModelScenario[],
  auditLog: PropertyAuditRecord[]
): SupabasePropertyPayload => ({
  display_order: normalizeSupabaseInteger(displayOrder, 1),
  name: name.trim() || 'Untitled Property',
  location: address.trim() || null,
  ownership_entity: ownershipEntity.trim() || null,
  base_year: normalizeSupabaseInteger(baseYear),
  purchase_price: normalizeSupabaseNumber(propertyInputs.purchasePrice),
  equity_contribution: normalizeSupabaseNumber(propertyInputs.downPayment),
  annual_rent: normalizeSupabaseNumber(propertyInputs.annualRent),
  annual_taxes: normalizeSupabaseNumber(propertyInputs.annualTaxes),
  annual_insurance: normalizeSupabaseNumber(propertyInputs.annualInsurance),
  annual_maintenance: normalizeSupabaseNumber(propertyInputs.annualMaintenance),
  vacancy_rate: normalizeSupabaseNumber(propertyInputs.vacancyRate),
  rent_growth_rate: normalizeSupabaseNumber(propertyInputs.rentGrowth),
  expense_growth_rate: normalizeSupabaseNumber(propertyInputs.expenseGrowth),
  appreciation_rate: normalizeSupabaseNumber(propertyInputs.appreciationRate),
  exit_cap_rate: normalizeSupabaseNumber(propertyInputs.exitCapRate),
  exit_costs: normalizeSupabaseNumber(propertyInputs.saleCosts),
  hold_period: normalizeSupabaseInteger(propertyInputs.holdPeriod),
  current_year_inputs: propertyInputs,
  historical_actuals: historicalActuals,
  risk_profile: riskProfile,
  model_events: modelEvents,
  saved_scenarios: stripDerivedScenarioFields(savedScenarios),
  audit_log: auditLog
});

const mergeSupabaseInputs = (record: SupabasePropertyRow, existingInputs?: FinancialInputs): FinancialInputs => {
  const baseInputs = record.current_year_inputs
    ? {
        ...createPersistedPropertyInputs(),
        ...(asObject(record.current_year_inputs, createPersistedPropertyInputs() as unknown as Record<string, unknown>) as Partial<FinancialInputs>)
      }
    : existingInputs
      ? { ...existingInputs }
      : createPersistedPropertyInputs();
  return {
    ...baseInputs,
    purchasePrice: record.purchase_price ?? baseInputs.purchasePrice,
    downPayment: record.equity_contribution ?? baseInputs.downPayment,
    annualRent: record.annual_rent ?? baseInputs.annualRent,
    annualTaxes: record.annual_taxes ?? baseInputs.annualTaxes,
    annualInsurance: record.annual_insurance ?? baseInputs.annualInsurance,
    annualMaintenance: record.annual_maintenance ?? baseInputs.annualMaintenance,
    vacancyRate: record.vacancy_rate ?? baseInputs.vacancyRate,
    rentGrowth: record.rent_growth_rate ?? baseInputs.rentGrowth,
    expenseGrowth: record.expense_growth_rate ?? baseInputs.expenseGrowth,
    appreciationRate: record.appreciation_rate ?? baseInputs.appreciationRate,
    exitCapRate: record.exit_cap_rate ?? baseInputs.exitCapRate,
    saleCosts: record.exit_costs ?? baseInputs.saleCosts,
    holdPeriod: Math.max(1, Math.round(record.hold_period ?? baseInputs.holdPeriod)),
    landAllocationPercent: baseInputs.landAllocationPercent ?? 20,
    buildingAllocationPercent: baseInputs.buildingAllocationPercent ?? 80,
    capitalImprovements: baseInputs.capitalImprovements ?? 0,
    placedInServiceDate: baseInputs.placedInServiceDate ?? '',
    depreciationMethod: baseInputs.depreciationMethod ?? 'residential_rental_sl_27_5',
    reserveInputsVersion: 1,
    investmentInputsVersion: 1
  };
};

const getHydratedPropertyDetails = (
  record: SupabasePropertyRow,
  matchedProperty?: {
    baseYear: number;
    historicalActuals: HistoricalActualYear[];
    riskProfile: PropertyRiskProfile;
    modelEvents: PropertyModelEvent[];
    savedScenarios: SavedModelScenario[];
    auditLog: PropertyAuditRecord[];
  } | null
) => ({
  baseYear: Math.max(1, Math.round(record.base_year ?? matchedProperty?.baseYear ?? INITIAL_BASE_YEAR)),
  historicalActuals: ((asArray(record.historical_actuals) as HistoricalActualYear[] | null) ?? matchedProperty?.historicalActuals) || [],
  riskProfile: normalizeRiskProfile(record.risk_profile || matchedProperty?.riskProfile || createDefaultRiskProfile()),
  modelEvents: ((asArray(record.model_events) as PropertyModelEvent[] | null) ?? matchedProperty?.modelEvents) || [],
  savedScenarios: stripDerivedScenarioFields((((asArray(record.saved_scenarios) as SavedModelScenario[] | null) ?? matchedProperty?.savedScenarios) || [])),
  auditLog: ((asArray(record.audit_log) as PropertyAuditRecord[] | null) ?? matchedProperty?.auditLog) || []
});

const buildSummaryPropertyRecord = (
  record: Pick<SupabasePropertyRow, 'id' | 'created_at' | 'updated_at' | 'team_id' | 'name' | 'location' | 'ownership_entity' | 'base_year' | 'purchase_price' | 'equity_contribution' | 'annual_rent'>,
  matchedProperty?: Property | null
): Property => {
  const baseInputs = matchedProperty?.currentYearInputs
    ? { ...matchedProperty.currentYearInputs }
    : createPersistedPropertyInputs();
  const canonicalId = matchedProperty?.id || record.id;
  const nextInputs: FinancialInputs = {
    ...baseInputs,
    purchasePrice: record.purchase_price ?? baseInputs.purchasePrice,
    downPayment: record.equity_contribution ?? baseInputs.downPayment,
    annualRent: record.annual_rent ?? baseInputs.annualRent
  };

  return {
    id: canonicalId,
    supabaseId: record.id,
    teamId: record.team_id,
    detailLevel: matchedProperty?.detailLevel === 'full' ? 'full' : 'summary',
    ownershipEntity: record.ownership_entity || matchedProperty?.ownershipEntity || '',
    name: record.name?.trim() || matchedProperty?.name || 'Untitled Property',
    address: record.location || matchedProperty?.address || '',
    createdAt: matchedProperty?.createdAt || (record.created_at ? new Date(record.created_at).getTime() : Date.now()),
    updatedAt: record.updated_at ? new Date(record.updated_at).getTime() : (matchedProperty?.updatedAt || Date.now()),
    baseYear: Math.max(1, Math.round(record.base_year ?? matchedProperty?.baseYear ?? INITIAL_BASE_YEAR)),
    currentYearInputs: nextInputs,
    historicalActuals: matchedProperty?.historicalActuals || [],
    inputs: nextInputs,
    riskProfile: matchedProperty?.riskProfile || createDefaultRiskProfile(),
    modelEvents: matchedProperty?.modelEvents || [],
    savedScenarios: matchedProperty?.savedScenarios || [],
    auditLog: matchedProperty?.auditLog || [],
    metrics: matchedProperty?.metrics || ({} as Property['metrics'])
  };
};

const buildSupabasePropertyDebtPayload = (propertyId: string, debt: DebtRecord) => ({
  property_id: propertyId,
  ownership_entity: debt.ownershipEntity?.trim() || null,
  is_shared_portfolio_debt: Boolean(debt.isSharedPortfolioDebt),
  loan_name: debt.name || 'Portfolio Loan',
  loan_type: debt.loanType,
  lender: debt.lender || null,
  loan_amount: normalizeSupabaseNumber(debt.originalBalance),
  current_balance: normalizeSupabaseNumber(debt.currentBalance),
  interest_rate: normalizeSupabaseNumber(debt.interestRate),
  amort_years: normalizeSupabaseInteger(debt.amortYears || 0, 0),
  loan_term_years: normalizeSupabaseInteger(debt.amortYears || 0, 0),
  monthly_payment: normalizeSupabaseNumber(debt.monthlyPayment),
  start_date: normalizeNullableIsoDate(debt.originationDate),
  first_payment_date: normalizeNullableIsoDate(debt.firstPaymentDate),
  payment_day_of_month: normalizeSupabaseInteger(debt.paymentDayOfMonth || 1),
  maturity_date: normalizeNullableIsoDate(debt.maturityDate),
  interest_only: Boolean(debt.interestOnly),
  interest_only_months: normalizeSupabaseInteger(debt.interestOnlyMonths || 0, 0),
  balloon_date: normalizeNullableIsoDate(debt.balloonDate),
  balloon_amount: normalizeSupabaseNumber(debt.balloonAmount),
  credit_limit: normalizeSupabaseNumber(debt.creditLimit),
  assume_scheduled_payments_made: debt.assumeScheduledPaymentsMade !== false,
  notes: debt.notes || null
});

const buildSupabasePropertyDebtLinkPayload = (link: PropertyDebtLink) => ({
  debt_id: link.debtId,
  property_id: link.propertyId,
  allocation_percent: normalizeSupabaseNumber(link.allocationPercent),
  allocation_method: link.allocationMethod,
  value_basis: link.valueBasis || 'purchase_price',
  manual_value_override: link.manualValueOverride == null ? null : normalizeSupabaseNumber(link.manualValueOverride)
});

const mapSupabaseDebtRowToRecord = (row: SupabasePropertyDebtRow, existing?: DebtRecord | null) => normalizeDebtRecord({
  id: row.id,
  supabaseId: row.id,
  propertyId: row.property_id,
  teamId: row.team_id,
  ownershipEntity: row.ownership_entity || existing?.ownershipEntity || '',
  isSharedPortfolioDebt: row.is_shared_portfolio_debt ?? existing?.isSharedPortfolioDebt ?? false,
  name: row.loan_name || 'Portfolio Loan',
  loanType: (row.loan_type as DebtRecord['loanType'] | null) || 'amortizing',
  lender: row.lender || '',
  originationDate: row.start_date || '',
  firstPaymentDate: row.first_payment_date || '',
  paymentDayOfMonth: row.payment_day_of_month || 1,
  paymentFrequency: 'monthly',
  originalBalance: row.loan_amount || 0,
  currentBalance: row.current_balance || row.loan_amount || 0,
  interestRate: row.interest_rate || 0,
  amortYears: row.amort_years || 0,
  interestOnly: Boolean(row.interest_only),
  interestOnlyMonths: row.interest_only_months || 0,
  assumeScheduledPaymentsMade: row.assume_scheduled_payments_made !== false,
  maturityDate: row.maturity_date || '',
  balloonDate: row.balloon_date || '',
  balloonAmount: row.balloon_amount || 0,
  creditLimit: row.credit_limit || 0,
  monthlyPayment: row.monthly_payment || 0,
  notes: row.notes || '',
  createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now(),
  updatedAt: row.created_at ? new Date(row.created_at).getTime() : Date.now()
});

const mapSupabaseDebtLinkRowToRecord = (row: SupabasePropertyDebtLinkRow, existing?: PropertyDebtLink | null): PropertyDebtLink => ({
  id: row.id,
  debtId: row.debt_id,
  propertyId: row.property_id,
  teamId: row.team_id,
  allocationPercent: normalizeSupabaseNumber(row.allocation_percent),
  allocationMethod: (row.allocation_method as PropertyDebtLink['allocationMethod'] | null) || 'manual',
  valueBasis: (row.value_basis as PropertyDebtLink['valueBasis'] | null) || existing?.valueBasis || 'purchase_price',
  manualValueOverride: row.manual_value_override == null ? (existing?.manualValueOverride ?? null) : normalizeSupabaseNumber(row.manual_value_override)
});

const mapSupabaseDebtEventRowToRecord = (row: SupabaseDebtEventRow): DebtEvent => ({
  id: row.id,
  debtId: row.debt_id,
  teamId: row.team_id,
  date: row.event_date || new Date().toISOString().slice(0, 10),
  type: (row.event_type as DebtEvent['type'] | null) || 'extra_principal_payment',
  amount: normalizeSupabaseNumber(row.amount),
  notes: row.notes || '',
  createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now(),
  updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : (row.created_at ? new Date(row.created_at).getTime() : Date.now())
});

const mapSupabasePortfolioRowToRecord = (row: SupabasePortfolioRow): Portfolio => ({
  id: row.id,
  teamId: row.team_id,
  name: row.name?.trim() || 'Untitled Portfolio',
  propertyIds: [],
  createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now(),
  updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : (row.created_at ? new Date(row.created_at).getTime() : Date.now())
});

const mapSupabaseTeamRowToRecord = (row: SupabaseTeamRow, role: TeamRole, propertyCount = 0): Team => ({
  id: row.id,
  name: row.name?.trim() || 'Shared Team',
  role,
  isPersonal: Boolean(row.is_personal),
  ownerUserId: row.owner_user_id,
  propertyCount,
  createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now()
});

const mapSupabaseTeamMembershipToMember = (
  row: SupabaseTeamMembershipRow,
  profileMap: Record<string, SupabaseUserProfileRow>
): TeamMember => ({
  id: row.id,
  teamId: row.team_id,
  userId: row.user_id,
  role: row.role || 'viewer',
  email: profileMap[row.user_id]?.email ?? null,
  displayName: profileMap[row.user_id]?.display_name ?? null,
  createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now()
});

const debugPropertySync = (label: string, details: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  if (window.localStorage.getItem('debug_property_sync') !== '1') return;
  console.info(`[Property Sync] ${label}`, details);
};

const INIT_STEP_STORE_KEY = '__WRENNEN_INIT_STEPS__';
const recordInitStep = (label: string, details: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined') return;
  const existing = (window as any)[INIT_STEP_STORE_KEY] || {};
  (window as any)[INIT_STEP_STORE_KEY] = {
    ...existing,
    [label]: {
      at: Date.now(),
      ...details
    }
  };
  console.info(label, details);
};

const resolveSupabasePropertyId = (
  propertyId: string | null | undefined,
  properties: Record<string, any>
) => {
  if (!propertyId) return null;
  const directProperty = properties[propertyId];
  if (directProperty) {
    return directProperty.supabaseId ?? null;
  }
  const matchedProperty = Object.values(properties).find((property: any) => property.supabaseId === propertyId);
  return matchedProperty?.supabaseId ?? propertyId;
};

const applyOwnershipScope = (query: any, userId: string, teamId: string | null) => (
  teamId ? query.eq('team_id', teamId) : query.eq('user_id', userId)
);

const buildOwnershipPayload = (userId: string, teamId: string | null) => (
  teamId ? { user_id: userId, team_id: teamId } : { user_id: userId }
);

const selectSingleSupabaseProperty = async (propertyId: string, userId: string, teamId: string | null) => {
  debugPropertySync('Lookup start', {
    eqId: propertyId,
    eqUserId: userId,
    eqTeamId: teamId
  });
  const scopedQuery = applyOwnershipScope(
    supabase
    .from('properties')
    .select(SUPABASE_PROPERTY_COLUMNS)
    .eq('id', propertyId),
    userId,
    teamId
  );
  const { data, error } = await scopedQuery.maybeSingle();

  debugPropertySync('Lookup result', {
    eqId: propertyId,
    eqUserId: userId,
    eqTeamId: teamId,
    returnedRowId: data?.id ?? null,
    returnedUserId: data?.user_id ?? null,
    returnedTeamId: data?.team_id ?? null,
    error: error?.message ?? null
  });

  return {
    data: (data as SupabasePropertyRow | null) ?? null,
    error
  };
};

const getRiskTone = (score: number) => {
  if (score >= 70) return { label: 'High Risk', badge: 'border-rose-500/40 bg-rose-500/10 text-rose-300', accent: 'text-rose-300' };
  if (score >= 45) return { label: 'Moderate Risk', badge: 'border-amber-500/40 bg-amber-500/10 text-amber-300', accent: 'text-amber-300' };
  return { label: 'Stable', badge: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300', accent: 'text-emerald-300' };
};

const App = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === 'undefined') return 'dark' as ThemeMode;
    const storedTheme = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme;
    return 'dark' as ThemeMode;
  });
  const [showLanding, setShowLanding] = useState(false);
  const [authMode, setAuthMode] = useState(null as null | 'login' | 'signup');
  const [activePropertyId, setActivePropertyId] = useState(null as string | null);
  const [activePortfolioId, setActivePortfolioId] = useState(null as string | null);
  const [propertyName, setPropertyName] = useState('New Asset Opportunity');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyOwnershipEntity, setPropertyOwnershipEntity] = useState('');
  const [baseYear, setBaseYear] = useState(INITIAL_BASE_YEAR);
  const [inputs, setInputs] = useState(createInitialInputs as () => FinancialInputs);
  const [historicalActuals, setHistoricalActuals] = useState([] as HistoricalActualYear[]);
  const [riskProfile, setRiskProfile] = useState(createDefaultRiskProfile() as PropertyRiskProfile);
  const [modelEvents, setModelEvents] = useState([] as PropertyModelEvent[]);
  const [savedScenarios, setSavedScenarios] = useState([] as SavedModelScenario[]);
  const [auditLog, setAuditLog] = useState([] as PropertyAuditRecord[]);
  const [propertyViewMode, setPropertyViewMode] = useState('create-core' as PropertyViewMode);
  const [activeTab, setActiveTab] = useState('dashboard' as 'dashboard' | 'analysis' | 'risk');
  const [activeWorkspace, setActiveWorkspace] = useState('property' as 'property' | 'portfolio' | 'debt' | 'tax');
  const [lastSavedAt, setLastSavedAt] = useState(null as number | null);
  const [chartRangeMonths, setChartRangeMonths] = useState(24);
  const [selectedAnalysisYear, setSelectedAnalysisYear] = useState(1);
  const [selectedTaxYear, setSelectedTaxYear] = useState(new Date().getFullYear());
  const [dashboardDetailView, setDashboardDetailView] = useState('simple' as DashboardDetailView);
  const [propertyDebtViewMode, setPropertyDebtViewMode] = useState('include_allocated_shared' as 'actual_only' | 'include_allocated_shared');
  const [timelineEditorMode, setTimelineEditorMode] = useState(null as null | 'create-actual' | 'edit-actual' | 'edit-current');
  const [timelineEditorYearId, setTimelineEditorYearId] = useState(null as string | null);
  const [hasInitializedWorkspace, setHasInitializedWorkspace] = useState(false);
  const [propertySyncStatus, setPropertySyncStatus] = useState(null as PropertySyncStatus);
  const [isSavingProperty, setIsSavingProperty] = useState(false);
  const [loadedSupabaseScopeKey, setLoadedSupabaseScopeKey] = useState(null as string | null);
  const [workspaceSummaryLoadState, setWorkspaceSummaryLoadState] = useState('idle' as 'idle' | 'loading' | 'ready' | 'error');
  const [debtLayerLoadState, setDebtLayerLoadState] = useState('idle' as 'idle' | 'loading' | 'ready' | 'error');
  const [hydratingPropertyIds, setHydratingPropertyIds] = useState([] as string[]);
  const [teams, setTeams] = useState([] as Team[]);
  const [teamMembers, setTeamMembers] = useState([] as TeamMember[]);
  const [activeTeamId, setActiveTeamId] = useState(null as string | null);
  const [selectedDebtId, setSelectedDebtId] = useState(null as string | null);
  const [teamStatus, setTeamStatus] = useState(null as string | null);
  const [bootTimeoutElapsed, setBootTimeoutElapsed] = useState(false);
  const isLoadingSupabasePropertiesRef = useRef(false);
  const workspaceSummaryRequestRef = useRef(0);
  const debtLayerRequestRef = useRef(0);

  const {
    properties, portfolios, propertyOrder, saveProperty, deleteProperty,
    createPortfolio, deletePortfolio, replaceProperties, replacePortfolios, replacePortfolioMemberships, reorderProperties,
    addPropertyToPortfolio, removePropertyFromPortfolio,
    debts, debtLinks, debtEvents, saveDebt, deleteDebt, replacePropertyDebts, replaceDebts, saveDebtEvent, deleteDebtEvent, replaceDebtEvents, upsertDebtLink, replaceDebtLinks, removeDebtLink, autoAllocateDebtByAcquisitionPrice, resetWorkspaceData, isLoaded
  } = usePropertyStore();

  useEffect(() => {
    document.documentElement.dataset.theme = user ? themeMode : 'dark';
    if (typeof window !== 'undefined' && user) {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
    }
  }, [themeMode, user]);

  useEffect(() => {
    if (user) {
      setAuthMode(null);
      setShowLanding(false);
      if (!activePropertyId && !activePortfolioId) {
        setActiveWorkspace('property');
        setPropertyViewMode('dashboard');
        setActiveTab('dashboard');
      }
    }
  }, [activePortfolioId, activePropertyId, user]);

  useEffect(() => {
    recordInitStep(`INIT loading authLoading=${authLoading ? 'true' : 'false'}`, {
      authLoading,
      bootTimeoutElapsed
    });
  }, [authLoading, bootTimeoutElapsed]);

  useEffect(() => {
    if (!authLoading) {
      recordInitStep('INIT FINAL set initializing false', {
        reason: bootTimeoutElapsed ? 'timeout fallback already released shell' : 'auth loading cleared'
      });
      return;
    }

    recordInitStep('INIT loading true', {
      authLoading,
      bootTimeoutElapsed
    });

    const timeoutId = window.setTimeout(() => {
      const completedSteps = typeof window === 'undefined'
        ? {}
        : ((window as any)[INIT_STEP_STORE_KEY] || {});

      console.warn('INIT TIMEOUT forcing initializing false after 8000ms', {
        completedSteps
      });
      setBootTimeoutElapsed(true);
      recordInitStep('INIT FINAL set initializing false', {
        reason: 'timeout fallback',
        completedSteps
      });
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authLoading, bootTimeoutElapsed]);

  useEffect(() => {
    if (!user) {
      setLoadedSupabaseScopeKey(null);
      setWorkspaceSummaryLoadState('idle');
      setDebtLayerLoadState('idle');
      setHydratingPropertyIds([]);
      setTeams([]);
      setTeamMembers([]);
      setActiveTeamId(null);
      setSelectedDebtId(null);
      setTeamStatus(null);
      setPropertySyncStatus(null);
      resetWorkspaceData();
    }
  }, [resetWorkspaceData, user]);

  const activeDataScopeKey = useMemo(() => (
    user ? `${user.uid}:${activeTeamId || 'legacy-user'}` : null
  ), [activeTeamId, user]);
  const isWorkspaceReloading = Boolean(
    user &&
    activeTeamId &&
    activeDataScopeKey &&
    loadedSupabaseScopeKey !== activeDataScopeKey
  );

  useEffect(() => {
    if (!user) return;

    let isCancelled = false;

    const ensureAndLoadTeams = async () => {
      try {
        setTeamStatus('Preparing your team workspace...');
        const { data: ensuredTeamId, error: ensureError } = await supabase.rpc('ensure_personal_team', {
          profile_user_id: user.uid,
          profile_email: user.email,
          profile_display_name: user.displayName
        });

        if (ensureError) {
          if (!isCancelled) {
            setTeamStatus(`Could not prepare team workspace: ${ensureError.message}`);
          }
          return;
        }

        const { data: membershipRows, error: membershipError } = await supabase
          .from('team_memberships')
          .select(SUPABASE_TEAM_MEMBERSHIP_COLUMNS)
          .eq('user_id', user.uid)
          .order('created_at', { ascending: true });

        if (isCancelled) return;

        if (membershipError) {
          setTeamStatus(`Could not load team memberships: ${membershipError.message}`);
          return;
        }

        const memberships = (membershipRows as SupabaseTeamMembershipRow[] | null) || [];
        const teamIds = memberships.map((membership) => membership.team_id);
        if (teamIds.length === 0) {
          setTeams([]);
          setActiveTeamId(null);
          setTeamStatus('No team memberships found yet.');
          return;
        }

        const { data: teamRows, error: teamError } = await supabase
          .from('teams')
          .select(SUPABASE_TEAM_COLUMNS)
          .in('id', teamIds)
          .order('created_at', { ascending: true });

        if (isCancelled) return;

        if (teamError) {
          setTeamStatus(`Could not load teams: ${teamError.message}`);
          return;
        }

        const { data: propertyTeamRows, error: propertyTeamError } = await supabase
          .from('properties')
          .select('team_id')
          .in('team_id', teamIds);

        if (isCancelled) return;

        if (propertyTeamError) {
          setTeamStatus(`Could not load team record counts: ${propertyTeamError.message}`);
          return;
        }

        const membershipRoleMap = Object.fromEntries(
          memberships.map((membership) => [membership.team_id, membership.role || 'viewer'])
        ) as Record<string, TeamRole>;
        const propertyCountByTeamId = (((propertyTeamRows as Array<{ team_id: string | null }> | null) || []).reduce((counts, row) => {
          if (!row.team_id) return counts;
          counts[row.team_id] = (counts[row.team_id] || 0) + 1;
          return counts;
        }, {} as Record<string, number>));
        const nextTeams = ((teamRows as SupabaseTeamRow[] | null) || [])
          .map((row) => mapSupabaseTeamRowToRecord(row, membershipRoleMap[row.id] || 'viewer', propertyCountByTeamId[row.id] || 0))
          .sort((left, right) => {
            if (left.propertyCount !== right.propertyCount) return right.propertyCount - left.propertyCount;
            const leftOwnedPersonal = left.isPersonal && left.ownerUserId === user.uid;
            const rightOwnedPersonal = right.isPersonal && right.ownerUserId === user.uid;
            if (leftOwnedPersonal !== rightOwnedPersonal) return leftOwnedPersonal ? 1 : -1;
            if (left.isPersonal !== right.isPersonal) return left.isPersonal ? 1 : -1;
            return left.name.localeCompare(right.name);
          });
        const storedTeamId = typeof window === 'undefined' ? null : window.localStorage.getItem(ACTIVE_TEAM_STORAGE_KEY);
        const nextActiveTeamId = nextTeams.some((team) => team.id === activeTeamId)
          ? activeTeamId
          : nextTeams.some((team) => team.id === storedTeamId)
            ? storedTeamId
            : nextTeams.some((team) => team.id === ensuredTeamId)
              ? String(ensuredTeamId)
              : nextTeams[0]?.id || null;

        setTeams(nextTeams);
        setActiveTeamId(nextActiveTeamId);
        setTeamStatus(null);
      } catch (error) {
        if (!isCancelled) {
          setTeamStatus(`Could not load teams: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`);
        }
      }
    };

    void ensureAndLoadTeams();

    return () => {
      isCancelled = true;
    };
  }, [activeTeamId, user]);

  useEffect(() => {
    if (!activeTeamId) return;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_TEAM_STORAGE_KEY, activeTeamId);
    }
  }, [activeTeamId]);

  useEffect(() => {
    if (!user || !activeTeamId) {
      setTeamMembers([]);
      return;
    }

    let isCancelled = false;

    const loadActiveTeamMembers = async () => {
      try {
        const { data: membershipRows, error: membershipError } = await supabase
          .from('team_memberships')
          .select(SUPABASE_TEAM_MEMBERSHIP_COLUMNS)
          .eq('team_id', activeTeamId)
          .order('created_at', { ascending: true });

        if (isCancelled) return;

        if (membershipError) {
          setTeamStatus(`Could not load team members: ${membershipError.message}`);
          return;
        }

        const memberships = (membershipRows as SupabaseTeamMembershipRow[] | null) || [];
        const memberIds = memberships.map((membership) => membership.user_id);
        const { data: profileRows, error: profileError } = memberIds.length > 0
          ? await supabase.from('user_profiles').select(SUPABASE_USER_PROFILE_COLUMNS).in('id', memberIds)
          : { data: [], error: null };

        if (isCancelled) return;

        if (profileError) {
          setTeamStatus(`Could not load member profiles: ${profileError.message}`);
          return;
        }

        const profileMap = Object.fromEntries(
          (((profileRows as SupabaseUserProfileRow[] | null) || []).map((row) => [row.id, row]))
        ) as Record<string, SupabaseUserProfileRow>;
        setTeamMembers(memberships.map((membership) => mapSupabaseTeamMembershipToMember(membership, profileMap)));
      } catch (error) {
        if (!isCancelled) {
          setTeamStatus(`Could not load team members: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`);
        }
      }
    };

    void loadActiveTeamMembers();

    return () => {
      isCancelled = true;
    };
  }, [activeTeamId, user]);

  useEffect(() => {
    setLoadedSupabaseScopeKey(null);
    setWorkspaceSummaryLoadState(activeTeamId ? 'loading' : 'idle');
    setDebtLayerLoadState('idle');
    setHydratingPropertyIds([]);
    if (activeTeamId) {
      setPropertySyncStatus({
        tone: 'info',
        message: 'Switched team workspace. Reloading shared records...'
      });
    }
  }, [activeTeamId]);

  useEffect(() => {
    if (!activeDataScopeKey || loadedSupabaseScopeKey !== activeDataScopeKey) return;
    if (activePropertyId && !properties[activePropertyId]) {
      setActivePropertyId(null);
    }
    if (activePortfolioId && !portfolios[activePortfolioId]) {
      setActivePortfolioId(null);
    }
    if (selectedDebtId && !debts[selectedDebtId]) {
      setSelectedDebtId(null);
    }
  }, [activeDataScopeKey, activePortfolioId, activePropertyId, debts, loadedSupabaseScopeKey, portfolios, properties, selectedDebtId]);

  const activeSupabasePropertyId = useMemo(() => (
    resolveSupabasePropertyId(activePropertyId, properties)
  ), [activePropertyId, properties]);
  const activePropertyIsHydrated = useMemo(() => (
    activePropertyId ? properties[activePropertyId]?.detailLevel === 'full' : false
  ), [activePropertyId, properties]);
  const isActivePropertyHydrating = useMemo(() => (
    activePropertyId ? hydratingPropertyIds.includes(activePropertyId) : false
  ), [activePropertyId, hydratingPropertyIds]);
  const isActivePortfolioHydrating = useMemo(() => {
    if (!activePortfolioId) return false;
    const portfolio = portfolios[activePortfolioId];
    if (!portfolio) return false;
    return portfolio.propertyIds.some((propertyId: string) => hydratingPropertyIds.includes(propertyId) || properties[propertyId]?.detailLevel !== 'full');
  }, [activePortfolioId, hydratingPropertyIds, portfolios, properties]);
  const isTaxWorkspaceHydrating = useMemo(() => (
    activeWorkspace === 'tax'
      && (Object.values(properties) as Property[]).some((property: Property) => property.detailLevel !== 'full' || hydratingPropertyIds.includes(property.id))
  ), [activeWorkspace, hydratingPropertyIds, properties]);
  const activeDebtScopePropertyId = activeSupabasePropertyId || activePropertyId;
  const activePropertyDebtSnapshot = useMemo(() => {
    if (!activeDebtScopePropertyId) return null;
    return buildDebtSnapshotForProperty(
      activeDebtScopePropertyId,
      properties,
      debts,
      debtLinks,
      debtEvents,
      undefined,
      { includeSharedPortfolioDebt: propertyDebtViewMode === 'include_allocated_shared' }
    );
  }, [activeDebtScopePropertyId, properties, debts, debtLinks, debtEvents, propertyDebtViewMode]);
  const activeExistingProperty = useMemo(() => (
    activePropertyId ? properties[activePropertyId] || null : null
  ), [activePropertyId, properties]);
  const currentPropertyBaselineSignature = useMemo(() => (
    buildPropertyBaselineSignature({
      name: propertyName.trim() || 'Untitled Property',
      address: propertyAddress,
      baseYear,
      inputs,
      historicalActuals,
      riskProfile
    })
  ), [baseYear, historicalActuals, inputs, propertyAddress, propertyName, riskProfile]);
  const existingPropertyBaselineSignature = useMemo(() => (
    activeExistingProperty
      ? buildPropertyBaselineSignature({
          name: activeExistingProperty.name || 'Untitled Property',
          address: activeExistingProperty.address || '',
          baseYear: activeExistingProperty.baseYear,
          inputs: activeExistingProperty.currentYearInputs || activeExistingProperty.inputs,
          historicalActuals: activeExistingProperty.historicalActuals || [],
          riskProfile: activeExistingProperty.riskProfile
        })
      : null
  ), [activeExistingProperty]);
  const propertyDashboardProperties = useMemo(() => (
    (Object.values(properties) as Array<(typeof properties)[string]>)
      .slice()
      .sort((left, right) => {
        const leftTimestamp = left.updatedAt || left.createdAt || 0;
        const rightTimestamp = right.updatedAt || right.createdAt || 0;
        return rightTimestamp - leftTimestamp;
      })
  ), [properties]);
  const teamPortfolioAssignmentCount = useMemo(() => (
    (Object.values(portfolios) as Portfolio[]).reduce((sum, portfolio) => sum + portfolio.propertyIds.length, 0)
  ), [portfolios]);
  const propertyDashboardEmptyState = useMemo(() => {
    if (propertyDashboardProperties.length > 0) {
      return {
        title: undefined,
        description: undefined
      };
    }
    if (workspaceSummaryLoadState === 'loading' || isWorkspaceReloading) {
      return {
        title: 'Loading team workspace',
        description: 'Shared properties are still being loaded for the selected team.'
      };
    }
    if (activeTeamId && teamPortfolioAssignmentCount > 0) {
      return {
        title: 'Team records need attention',
        description: 'This team has portfolio assignments but no matching properties loaded. That usually means older property rows still need their team ownership backfilled.'
      };
    }
    if (activeTeamId && Object.keys(portfolios).length > 0) {
      return {
        title: 'This team has no property records loaded',
        description: 'The selected team has shared workspace data, but property rows did not come back. That usually points to incomplete team migration or ownership mapping.'
      };
    }
    return {
      title: undefined,
      description: undefined
    };
  }, [activeTeamId, isWorkspaceReloading, portfolios, propertyDashboardProperties.length, teamPortfolioAssignmentCount, workspaceSummaryLoadState]);
  const workspaceSyncStatus = useMemo(() => {
    if (workspaceSummaryLoadState === 'loading' || isWorkspaceReloading) {
      return {
        tone: 'info' as const,
        label: 'Syncing Workspace',
        detail: 'Shared properties, portfolios, debt, and memberships are reloading.'
      };
    }
    if (teamStatus) {
      return {
        tone: 'error' as const,
        label: 'Needs Attention',
        detail: teamStatus
      };
    }
    if (propertySyncStatus?.tone === 'error') {
      return {
        tone: 'error' as const,
        label: 'Sync Issue',
        detail: propertySyncStatus.message
      };
    }
    if (activeTeamId && loadedSupabaseScopeKey === activeDataScopeKey) {
      return {
        tone: 'success' as const,
        label: 'Workspace Ready',
        detail: `Connected to the selected team workspace with ${propertyDashboardProperties.length} saved propert${propertyDashboardProperties.length === 1 ? 'y' : 'ies'}.`
      };
    }
    return {
      tone: 'info' as const,
      label: 'Waiting To Load',
      detail: 'Select a team workspace to load its shared records.'
    };
  }, [activeDataScopeKey, activeTeamId, isWorkspaceReloading, loadedSupabaseScopeKey, propertyDashboardProperties.length, propertySyncStatus, teamStatus, workspaceSummaryLoadState]);
  const propertyScopedDebts = useMemo(() => (
    activeDebtScopePropertyId
      ? Object.fromEntries(
          (Object.entries(debts) as Array<[string, DebtRecord]>).filter(([, debt]) => debt.propertyId === activeDebtScopePropertyId)
        ) as typeof debts
      : debts
  ), [activeDebtScopePropertyId, debts]);
  const propertyScopedDebtEvents = useMemo(() => {
    if (!activeDebtScopePropertyId) return debtEvents;
    const debtIds = new Set(Object.keys(propertyScopedDebts));
    return debtEvents.filter((event: typeof debtEvents[number]) => debtIds.has(event.debtId));
  }, [activeDebtScopePropertyId, debtEvents, propertyScopedDebts]);
  const propertyScopedDebtLinks = useMemo(() => {
    if (!activeDebtScopePropertyId) return debtLinks;
    const debtIds = new Set(Object.keys(propertyScopedDebts));
    return debtLinks.filter((link: typeof debtLinks[number]) => debtIds.has(link.debtId) && link.propertyId === activeDebtScopePropertyId);
  }, [activeDebtScopePropertyId, debtLinks, propertyScopedDebts]);
  const canPersistDebt = Boolean(user && activeSupabasePropertyId);
  const idleModelResult = useMemo(() => (
    buildModel(createInitialInputs(), createDefaultRiskProfile(), null, { baseYear: INITIAL_BASE_YEAR, historicalActuals: [] })
  ), []);
  const shouldBuildActivePropertyModel = activeWorkspace === 'property'
    && (propertyViewMode !== 'dashboard' || (Boolean(activePropertyId) && activePropertyIsHydrated));

  const modelResult = useMemo(() => (
    shouldBuildActivePropertyModel
      ? buildModel(inputs, riskProfile, activePropertyDebtSnapshot, { baseYear, historicalActuals })
      : idleModelResult
  ), [activePropertyDebtSnapshot, baseYear, historicalActuals, idleModelResult, inputs, riskProfile, shouldBuildActivePropertyModel]);
  const chartData = useMemo(() => {
    return modelResult.monthlyProjection.slice(0, Math.max(24, chartRangeMonths));
  }, [chartRangeMonths, modelResult.monthlyProjection]);
  const monthlyAnalysisRows = useMemo(() => {
    return modelResult.monthlyProjection.slice(0, Math.max(24, chartRangeMonths));
  }, [chartRangeMonths, modelResult.monthlyProjection]);

  const chartTicks = useMemo(() => {
    const baseTicks = [1, 6, 12, 18, 24, 36, 48, 60];
    return baseTicks.filter((tick) => tick <= chartRangeMonths);
  }, [chartRangeMonths]);
  const availableAnalysisYears = modelResult.proForma.length;
  const activeAnalysisYear = Math.max(1, Math.min(selectedAnalysisYear, availableAnalysisYears));
  const activeYearData = modelResult.proForma[Math.max(0, activeAnalysisYear - 1)];
  const activeCalendarYear = baseYear + activeAnalysisYear - 1;
  const activeYearTypeLabel = activeAnalysisYear === 1 ? 'Current' : 'Forecast';
  const activeYearMonths = useMemo(() => (
    modelResult.monthlyProjection.slice((activeAnalysisYear - 1) * 12, activeAnalysisYear * 12)
  ), [activeAnalysisYear, modelResult.monthlyProjection]);
  const activeYearMonthCount = Math.max(1, activeYearMonths.length);
  const hasMeaningfulInitialInvestment = modelResult.metrics.totalInitialInvestment > 0.005;
  const activeYearCoC = activeYearData && hasMeaningfulInitialInvestment
    ? (activeYearData.operatingCf / modelResult.metrics.totalInitialInvestment) * 100
    : null;
  const activeYearMonthlyCashFlow = activeYearData ? activeYearData.operatingCf / activeYearMonthCount : 0;
  const activeYearMonthlyTrueReturn = activeYearData ? activeYearData.trueReturn / activeYearMonthCount : 0;
  const activeYearTerminalLiquidity = activeYearData ? activeYearData.netSaleProceeds + activeYearData.reserveRelease : 0;
  const activeYearModeledValue = activeYearData?.assumedSalePrice || 0;
  const activeYearEndingDebt = activeYearData?.loanBalance || 0;
  const activeYearEquityValue = activeYearModeledValue - activeYearEndingDebt;
  const activeYearExitProfit = activeYearData && activeYearData.moic !== null && hasMeaningfulInitialInvestment
    ? (activeYearData.moic * modelResult.metrics.totalInitialInvestment) - modelResult.metrics.totalInitialInvestment
    : 0;
  const activeYearRepairReserveFunding = activeYearMonths.reduce((sum: number, row: MonthlyProjectionRow) => sum + row.repairReserve, 0);
  const activeYearTurnoverReserveFunding = activeYearMonths.reduce((sum: number, row: MonthlyProjectionRow) => sum + row.turnoverReserve, 0);
  const activeYearReserveFunding = activeYearRepairReserveFunding + activeYearTurnoverReserveFunding;
  const activeYearRepairReserveMonthly = activeYearRepairReserveFunding / activeYearMonthCount;
  const reserveFundingInputLabel = inputs.reserveFundingMode === 'percent' ? 'Reserve Funding % of Rent' : 'Annual CapEx Reserve';
  const reserveFundingTransferDescription = inputs.reserveFundingMode === 'percent'
    ? `${formatPercent2(inputs.reservePercentOfRent)} of scheduled rent is transferred into reserves.`
    : inputs.reserveFundingMode === 'off'
      ? 'Reserve funding is turned off, so no recurring reserve transfer is modeled.'
      : `${formatCurrency0(inputs.annualCapexReserve)} per year is transferred into reserves from the Annual CapEx Reserve input.`;
  const reserveTransferCards = activeYearTurnoverReserveFunding > 0
    ? [
        {
          label: 'Total Reserve Transfer',
          value: activeYearReserveFunding,
          tone: 'text-cyan-300',
          description: `Combined reserve transfers in Year ${activeAnalysisYear}.`
        },
        {
          label: 'Repair Reserve Transfer',
          value: activeYearRepairReserveFunding,
          tone: 'text-white',
          description: reserveFundingTransferDescription
        },
        {
          label: 'Turnover Reserve Transfer',
          value: activeYearTurnoverReserveFunding,
          tone: 'text-white',
          description: `Turnover-specific reserve transfers in Year ${activeAnalysisYear}.`
        }
      ]
    : [
        {
          label: 'Reserve Transfer',
          value: activeYearReserveFunding,
          tone: 'text-cyan-300',
          description: `${reserveFundingTransferDescription} Driven by ${reserveFundingInputLabel}.`
        }
      ];
  const activeYearOperatingCashFlowBeforeReserves = activeYearMonths.reduce((sum: number, row: MonthlyProjectionRow) => sum + row.operatingCashFlowBeforeReserves, 0);
  const activeYearNoi = activeYearData?.noi || 0;
  const activeYearEgi = activeYearData?.egi || 0;
  const activeYearCapRate = inputs.purchasePrice > 0 ? (activeYearNoi / inputs.purchasePrice) * 100 : null;
  const riskSummary = useMemo(() => (
    activeWorkspace === 'property' && activePropertyId ? computeRiskSummary(inputs, riskProfile, modelResult) : null
  ), [activePropertyId, activeWorkspace, inputs, modelResult, riskProfile]);
  const propertyRiskTone = getRiskTone(riskSummary?.overallRiskScore ?? 0);
  const priorYearData = activeAnalysisYear > 1 ? modelResult.proForma[activeAnalysisYear - 2] : null;
  const activeYearNoiGrowthRate = priorYearData && Math.abs(priorYearData.noi) > 0
    ? ((activeYearNoi - priorYearData.noi) / Math.abs(priorYearData.noi)) * 100
    : null;
  const financialSnapshotScores = useMemo(() => {
    if (!riskSummary) return null;

    return computeFinancialSnapshotScores({
      monthlyFreeCashFlow: riskSummary.monthlyFreeCashFlow,
      cashOnCashReturn: activeYearCoC,
      noiGrowthRate: activeYearNoiGrowthRate,
      overallRiskScore: riskSummary.overallRiskScore,
      debtPressure: riskSummary.debtPressure,
      liquidityRisk: riskSummary.liquidityRisk,
      capRate: activeYearCapRate,
      totalCashInvested: modelResult.metrics.totalInitialInvestment
    });
  }, [
    activeYearCapRate,
    activeYearCoC,
    activeYearNoiGrowthRate,
    modelResult.metrics.totalInitialInvestment,
    riskSummary
  ]);
  const financialSnapshotDescriptions = useMemo(() => {
    const performance = activeYearMonthlyCashFlow >= 250
      ? 'Strong monthly income'
      : activeYearMonthlyCashFlow >= 0
        ? 'Positive income, but not yet standout'
        : 'Income is being dragged below break-even';
    const stability = !riskSummary
      ? 'Link debt and reserves to assess stability'
      : riskSummary.debtPressure > 0.8
        ? 'High risk due to leverage'
        : riskSummary.liquidityRisk > 50
          ? 'Reserve position needs work'
          : 'Risk profile looks controlled';
    const efficiency = activeYearCoC !== null && activeYearCoC >= 12
      ? 'Capital is working efficiently'
      : activeYearCoC !== null && activeYearCoC >= 8
        ? 'Returns are healthy for the capital in'
        : hasMeaningfulInitialInvestment
          ? 'Capital efficiency is still soft'
          : 'Add real investor cash-in to score efficiency cleanly';

    return { performance, stability, efficiency };
  }, [activeYearCoC, activeYearMonthlyCashFlow, hasMeaningfulInitialInvestment, riskSummary]);
  const activeTeam = useMemo(() => (
    activeTeamId ? teams.find((team: Team) => team.id === activeTeamId) || null : null
  ), [activeTeamId, teams]);
  const activePortfolio = useMemo(() => (
    activePortfolioId ? portfolios[activePortfolioId] || null : null
  ), [activePortfolioId, portfolios]);
  const workspaceSummary = useMemo(() => {
    const propertyRecords = propertyDashboardProperties;
    const debtRecords = Object.values(debts) as DebtRecord[];
    const totalPurchasePrice = propertyRecords.reduce((sum: number, property: Property) => sum + (property.currentYearInputs?.purchasePrice || property.inputs.purchasePrice || 0), 0);
    const totalAnnualRent = propertyRecords.reduce((sum: number, property: Property) => sum + (property.currentYearInputs?.annualRent || property.inputs.annualRent || 0), 0);
    const totalCurrentDebt = debtRecords.reduce((sum, debt) => sum + (debt.currentBalance || 0), 0);
    const totalMonthlyDebtService = debtRecords.reduce((sum, debt) => sum + (debt.monthlyPayment || 0), 0);
    const topProperty = propertyRecords.reduce((currentTop: Property | null, property: Property) => {
      if (!currentTop) return property;
      const currentValue = currentTop.currentYearInputs?.purchasePrice || currentTop.inputs.purchasePrice || 0;
      const nextValue = property.currentYearInputs?.purchasePrice || property.inputs.purchasePrice || 0;
      return nextValue > currentValue ? property : currentTop;
    }, null as Property | null);
    const topDebt = debtRecords.reduce<DebtRecord | null>((currentTop, debt) => {
      if (!currentTop) return debt;
      return (debt.currentBalance || 0) > (currentTop.currentBalance || 0) ? debt : currentTop;
    }, null);

    return {
      activeTeamId: activeTeamId || null,
      activeTeamName: activeTeam?.name || null,
      teamRole: activeTeam?.role || null,
      propertyCount: propertyRecords.length,
      portfolioCount: Object.keys(portfolios).length,
      debtCount: debtRecords.length,
      teamMemberCount: teamMembers.length,
      totalPurchasePrice: propertyRecords.length > 0 ? totalPurchasePrice : null,
      totalAnnualRent: propertyRecords.length > 0 ? totalAnnualRent : null,
      totalCurrentDebt: debtRecords.length > 0 ? totalCurrentDebt : null,
      totalMonthlyDebtService: debtRecords.length > 0 ? totalMonthlyDebtService : null,
      selectedPortfolioName: activePortfolio?.name || null,
      selectedPortfolioPropertyCount: activePortfolio ? activePortfolio.propertyIds.length : null,
      topPropertyByPurchasePrice: topProperty?.name || null,
      topDebtByBalance: topDebt?.name || null
    };
  }, [activePortfolio, activeTeam, activeTeamId, debts, portfolios, propertyDashboardProperties, teamMembers.length]);
  const ownershipEntityOptions = useMemo(() => {
    const values = new Set<string>();
    (Object.values(properties) as Property[]).forEach((property: Property) => {
      const entity = (property.ownershipEntity || '').trim();
      if (entity) values.add(entity);
    });
    (Object.values(debts) as DebtRecord[]).forEach((debt: DebtRecord) => {
      const entity = (debt.ownershipEntity || '').trim();
      if (entity) values.add(entity);
    });
    return [...values].sort((left, right) => left.localeCompare(right));
  }, [debts, properties]);
  const askWrennenPortfolioDashboard = useMemo(() => {
    if (!activePortfolio) {
      return {
        portfolioId: null,
        portfolioName: null,
        memberCount: 0,
        valueDisplayBasis: null,
        annualCashFlowBasis: null,
        totals: {
          value: null,
          debt: null,
          equity: null,
          annualCashFlow: null
        },
        members: []
      };
    }

    const portfolioProperties = activePortfolio.propertyIds
      .map((id: string) => properties[id])
      .filter(Boolean) as Property[];

    const members = portfolioProperties.map((property: Property) => {
      const propertyInputs = property.currentYearInputs || property.inputs;
      const debtSnapshot = buildDebtSnapshotForProperty(property.id, properties, debts, debtLinks, debtEvents);
      const propertyModel = buildModel(
        { ...propertyInputs },
        property.riskProfile,
        debtSnapshot,
        { baseYear: property.baseYear, historicalActuals: property.historicalActuals },
        { modelEvents: property.modelEvents || [] }
      );
      const summary = computeRiskSummary(propertyInputs, property.riskProfile, propertyModel);
      const holdPeriodIndex = Math.max(0, propertyModel.proForma.length - 1);
      const value = propertyModel.proForma[holdPeriodIndex]?.assumedSalePrice ?? propertyInputs.purchasePrice ?? null;
      const debt = propertyModel.metrics.allocatedCurrentLoanBalance ?? null;
      const equity = value !== null && debt !== null ? value - debt : null;
      const annualCashFlow = propertyModel.proForma[0]?.operatingCf ?? null;

      return {
        propertyId: property.id,
        propertyName: property.name,
        value,
        debt,
        equity,
        riskScore: summary.overallRiskScore ?? null,
        annualCashFlow
      };
    });

    const totals = members.reduce((acc, member) => ({
      value: acc.value + (member.value || 0),
      debt: acc.debt + (member.debt || 0),
      equity: acc.equity + (member.equity || 0),
      annualCashFlow: acc.annualCashFlow + (member.annualCashFlow || 0)
    }), {
      value: 0,
      debt: 0,
      equity: 0,
      annualCashFlow: 0
    });

    return {
      portfolioId: activePortfolio.id,
      portfolioName: activePortfolio.name,
      memberCount: members.length,
      valueDisplayBasis: 'Portfolio dashboard Value uses each property model’s assumed sale price in the final hold-period year, not a Year 1 valuation.',
      annualCashFlowBasis: 'Portfolio dashboard Cash Flow uses current-year annual operating cash flow after debt service.',
      totals: members.length > 0 ? totals : {
        value: null,
        debt: null,
        equity: null,
        annualCashFlow: null
      },
      members
    };
  }, [activePortfolio, debts, debtEvents, debtLinks, properties]);
  const askWrennenContext = useMemo((): AskWrennenContext => ({
    workspace: activeWorkspace === 'property' ? (activePropertyId ? 'property' : 'general') : activeWorkspace,
    propertyId: activeWorkspace === 'property' ? activePropertyId : null,
    propertyName: activeWorkspace === 'property' ? propertyName : null,
    address: activeWorkspace === 'property' ? propertyAddress || null : null,
    selectedYear: activeWorkspace === 'property' ? activeAnalysisYear : null,
    selectedCalendarYear: activeWorkspace === 'property' ? activeCalendarYear : null,
    selectedYearSnapshot: {
      typeLabel: activeWorkspace === 'property' ? activeYearTypeLabel : null,
      rent: activeWorkspace === 'property' ? activeYearData?.grossRent ?? null : null,
      expenses: activeWorkspace === 'property' ? activeYearData?.expenses ?? null : null,
      noi: activeWorkspace === 'property' ? activeYearData?.noi ?? null : null,
      debtService: activeWorkspace === 'property' ? activeYearData?.debtService ?? null : null,
      netCashFlow: activeWorkspace === 'property' ? activeYearData?.operatingCf ?? null : null,
      saleProceeds: activeWorkspace === 'property' ? activeYearData?.netSaleProceeds ?? null : null,
      reserveRelease: activeWorkspace === 'property' ? activeYearData?.reserveRelease ?? null : null,
      terminalLiquidity: activeWorkspace === 'property' ? activeYearTerminalLiquidity : null,
      moic: activeWorkspace === 'property' ? activeYearData?.moic ?? null : null,
      leveredIrr: activeWorkspace === 'property' ? activeYearData?.irr ?? null : null,
      annualCashOnCash: activeWorkspace === 'property' ? activeYearCoC : null,
      principalPaid: activeWorkspace === 'property' ? activeYearData?.principalPaid ?? null : null,
      interestPaid: activeWorkspace === 'property' ? activeYearData?.interestPaid ?? null : null
    },
    workspaceSummary,
    intake: {
      purchasePrice: activeWorkspace === 'property' ? inputs.purchasePrice : null,
      equityContribution: activeWorkspace === 'property' ? inputs.downPayment : null,
      acquisitionCosts: activeWorkspace === 'property' ? inputs.acquisitionCosts : null,
      lenderFees: activeWorkspace === 'property' ? inputs.lenderFees : null,
      initialCapex: activeWorkspace === 'property' ? inputs.initialCapex : null
    },
    operations: {
      annualRent: activeWorkspace === 'property' ? inputs.annualRent : null,
      vacancyRate: activeWorkspace === 'property' ? inputs.vacancyRate : null,
      annualTaxes: activeWorkspace === 'property' ? inputs.annualTaxes : null,
      annualInsurance: activeWorkspace === 'property' ? inputs.annualInsurance : null,
      annualHoa: activeWorkspace === 'property' ? inputs.annualHoa : null,
      annualMaintenance: activeWorkspace === 'property' ? inputs.annualMaintenance : null,
      propertyManagementRate: activeWorkspace === 'property' ? inputs.propertyManagementRate : null,
      utilitiesMonthly: activeWorkspace === 'property' ? inputs.utilitiesMonthly : null,
      otherIncomeMonthly: activeWorkspace === 'property' ? inputs.otherIncomeMonthly : null
    },
    debt: {
      linkedDebtName: modelResult.debtSnapshot?.debtName || null,
      totalDebt: modelResult.metrics.allocatedCurrentLoanBalance || null,
      monthlyDebtService: modelResult.metrics.allocatedMonthlyDebtService || null,
      interestRate: modelResult.metrics.allocatedInterestRate || modelResult.debtSnapshot?.interestRate || null,
      amortYears: modelResult.debtSnapshot?.amortYears || null,
      allocationPercent: modelResult.metrics.allocationPercent || null
    },
    dashboard: {
      cashOnCashReturn: activeWorkspace === 'property' ? activeYearCoC : null,
      leveredIrr: activeWorkspace === 'property' ? activeYearData?.irr ?? null : null,
      moic: activeWorkspace === 'property' ? activeYearData?.moic || null : null,
      terminalLiquidity: activeWorkspace === 'property' ? activeYearTerminalLiquidity : null,
      monthlyCashFlow: activeWorkspace === 'property' ? activeYearMonthlyCashFlow : null,
      monthlyTrueReturn: activeWorkspace === 'property' ? activeYearMonthlyTrueReturn : null,
      principalPaid: activeWorkspace === 'property' ? activeYearData?.principalPaid || null : null,
      interestPaid: activeWorkspace === 'property' ? activeYearData?.interestPaid || null : null,
      reserveTarget: riskSummary?.recommendedReserve ?? null,
      reserveCurrent: riskSummary?.currentReserve ?? null
    },
    portfolioDashboard: askWrennenPortfolioDashboard,
    risk: {
      overallRiskScore: riskSummary?.overallRiskScore ?? null,
      resilienceScore: riskSummary?.resilienceScore ?? null,
      capexNext12Months: riskSummary?.capexNext12Months ?? null,
      capexNext24Months: riskSummary?.capexNext24Months ?? null,
      maintenanceRiskFlags: riskSummary?.maintenanceRiskFlags || [],
      stressTests: modelResult.metrics.stressTests || []
    }
  }), [
    activeAnalysisYear,
    activeCalendarYear,
    activePortfolio,
    activePropertyId,
    activeTeam,
    activeTeamId,
    activeWorkspace,
    activeYearCoC,
    activeYearData,
    activeYearMonthlyCashFlow,
    activeYearMonthlyTrueReturn,
    debts,
    activeYearTerminalLiquidity,
    activeYearTypeLabel,
    askWrennenPortfolioDashboard,
    inputs,
    modelResult,
    portfolios,
    propertyAddress,
    propertyDashboardProperties,
    propertyName,
    riskSummary,
    teamMembers.length,
    workspaceSummary
  ]);

  useEffect(() => {
    setSelectedAnalysisYear((prev: number) => Math.max(1, Math.min(prev, modelResult.proForma.length || 1)));
  }, [modelResult.proForma.length]);

  const updateBrowserPath = useCallback((path: string, replace = false) => {
    if (typeof window === 'undefined') return;
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', path);
  }, []);

  useEffect(() => {
    if (!isLoaded || hasInitializedWorkspace) return;
    recordInitStep('INIT workspace bootstrap start', {
      isLoaded,
      hasInitializedWorkspace,
      authenticatedUserId: user?.uid ?? null
    });
    setShowLanding(true);
    setActiveWorkspace('property');
    setActivePortfolioId(null);
    setActivePropertyId(null);
    setPropertyViewMode('create-core');
    setActiveTab('dashboard');
    updateBrowserPath('/', true);
    setHasInitializedWorkspace(true);
    recordInitStep('INIT workspace bootstrap done', {
      isLoaded,
      hasInitializedWorkspace: true,
      activeWorkspace: 'property',
      path: '/'
    });
  }, [hasInitializedWorkspace, isLoaded, updateBrowserPath, user]);

  const isCurrentSaved = Boolean(activePropertyId && properties[activePropertyId]);

  const updateInput = useCallback(<K extends keyof FinancialInputs>(key: K, value: FinancialInputs[K]) => {
    setInputs((prev: FinancialInputs) => ({ ...prev, [key]: value }));
  }, []);

  const persistPropertyState = useCallback((overrides?: {
    nextInputs?: FinancialInputs;
    nextHistoricalActuals?: HistoricalActualYear[];
    nextRiskProfile?: PropertyRiskProfile;
    nextModelEvents?: PropertyModelEvent[];
    nextSavedScenarios?: SavedModelScenario[];
    nextAuditLog?: PropertyAuditRecord[];
  }) => {
    if (!activePropertyId) return;
    const safeName = propertyName.trim() || 'Untitled Property';
    saveProperty(
      activePropertyId,
      safeName,
      propertyAddress,
      propertyOwnershipEntity,
      baseYear,
      overrides?.nextInputs || inputs,
      overrides?.nextHistoricalActuals || historicalActuals,
      overrides?.nextRiskProfile || riskProfile,
      overrides?.nextModelEvents || modelEvents,
      overrides?.nextSavedScenarios || savedScenarios,
      overrides?.nextAuditLog || auditLog,
      modelResult.metrics
    );
    setLastSavedAt(Date.now());
  }, [activePropertyId, auditLog, baseYear, historicalActuals, inputs, modelEvents, modelResult.metrics, propertyAddress, propertyName, propertyOwnershipEntity, riskProfile, saveProperty, savedScenarios]);

  const hydratePropertiesByIds = useCallback(async (propertyIds: string[]) => {
    if (!user || !isLoaded) return;

    const targetIds = propertyIds.filter((propertyId, index, list) => (
      list.indexOf(propertyId) === index && properties[propertyId]?.detailLevel !== 'full'
    ));

    if (targetIds.length === 0) return;

    const supabaseIds = targetIds
      .map((propertyId) => properties[propertyId]?.supabaseId || propertyId)
      .filter(Boolean);

    if (supabaseIds.length === 0) return;

    setHydratingPropertyIds((prev: string[]) => [...new Set([...prev, ...targetIds])]);

    try {
      const scopedQuery = applyOwnershipScope(
        supabase
          .from('properties')
          .select(SUPABASE_PROPERTY_COLUMNS),
        user.uid,
        activeTeamId
      );
      const { data, error } = await scopedQuery.in('id', supabaseIds);

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Property details could not be fetched: ${error.message}`
        });
        return;
      }

      const hydratedEntries = (((data as SupabasePropertyRow[] | null) || []).map((record) => {
        const matchedProperty = (Object.values(properties) as Property[]).find((property) => property.supabaseId === record.id) || properties[record.id] || null;
        const localPropertyId = matchedProperty?.id || record.id;
        const {
          baseYear: nextBaseYear,
          historicalActuals: nextHistoricalActuals,
          riskProfile: nextRiskProfile,
          modelEvents: nextModelEvents,
          savedScenarios: nextSavedScenarios,
          auditLog: nextAuditLog
        } = getHydratedPropertyDetails(record, matchedProperty);
        const nextInputs = mergeSupabaseInputs(record, matchedProperty?.currentYearInputs);

        return [localPropertyId, {
          id: localPropertyId,
          supabaseId: record.id,
          teamId: record.team_id,
          detailLevel: 'full' as const,
          ownershipEntity: record.ownership_entity || matchedProperty?.ownershipEntity || '',
          name: record.name?.trim() || matchedProperty?.name || 'Untitled Property',
          address: record.location || matchedProperty?.address || '',
          createdAt: matchedProperty?.createdAt || (record.created_at ? new Date(record.created_at).getTime() : Date.now()),
          updatedAt: record.updated_at ? new Date(record.updated_at).getTime() : Date.now(),
          baseYear: nextBaseYear,
          currentYearInputs: nextInputs,
          historicalActuals: nextHistoricalActuals,
          inputs: nextInputs,
          riskProfile: nextRiskProfile,
          modelEvents: nextModelEvents,
          savedScenarios: nextSavedScenarios,
          auditLog: nextAuditLog,
          metrics: matchedProperty?.metrics || ({} as Property['metrics'])
        }] as const;
      }));

      if (hydratedEntries.length > 0) {
        replaceProperties({
          ...properties,
          ...Object.fromEntries(hydratedEntries)
        });
      }
    } catch (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Property details could not be fetched: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`
      });
    } finally {
      setHydratingPropertyIds((prev: string[]) => prev.filter((propertyId: string) => !targetIds.includes(propertyId)));
    }
  }, [activeTeamId, isLoaded, properties, replaceProperties, user]);

  useEffect(() => {
    if (!user || !isLoaded || !activeDataScopeKey || loadedSupabaseScopeKey === activeDataScopeKey || isLoadingSupabasePropertiesRef.current) return;

    let isCancelled = false;
    const requestId = workspaceSummaryRequestRef.current + 1;
    workspaceSummaryRequestRef.current = requestId;

    const loadWorkspaceSummary = async () => {
      isLoadingSupabasePropertiesRef.current = true;
      setWorkspaceSummaryLoadState('loading');

      try {
        const propertyQuery = applyOwnershipScope(
          supabase.from('properties').select(SUPABASE_PROPERTY_SUMMARY_COLUMNS),
          user.uid,
          activeTeamId
        )
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        const portfolioQuery = applyOwnershipScope(
          supabase.from('portfolios').select(SUPABASE_PORTFOLIO_COLUMNS),
          user.uid,
          activeTeamId
        )
          .order('created_at', { ascending: true });

        const membershipQuery = applyOwnershipScope(
          supabase.from('portfolio_properties').select(SUPABASE_PORTFOLIO_PROPERTY_COLUMNS),
          user.uid,
          activeTeamId
        )
          .order('created_at', { ascending: true });

        const [propertyResult, portfolioResult, membershipResult] = await Promise.all([
          propertyQuery,
          portfolioQuery,
          membershipQuery
        ]);

        if (isCancelled || workspaceSummaryRequestRef.current !== requestId) return;

        if (propertyResult.error) {
          setWorkspaceSummaryLoadState('error');
          setPropertySyncStatus({
            tone: 'error',
            message: `Saved properties could not be loaded: ${propertyResult.error.message}`
          });
          return;
        }

        if (portfolioResult.error) {
          setWorkspaceSummaryLoadState('error');
          setPropertySyncStatus({
            tone: 'error',
            message: `Portfolios could not be fetched: ${portfolioResult.error.message}`
          });
          return;
        }

        if (membershipResult.error) {
          setWorkspaceSummaryLoadState('error');
          setPropertySyncStatus({
            tone: 'error',
            message: `Portfolio membership could not be fetched: ${membershipResult.error.message}`
          });
          return;
        }

        const summaryRows = (propertyResult.data as SupabasePropertyRow[] | null) || [];
        const nextPropertyEntries = summaryRows.map((record) => {
          const matchedProperty = (Object.values(properties) as Property[]).find((property) => property.supabaseId === record.id) || properties[record.id] || null;
          const summaryProperty = buildSummaryPropertyRecord(record, matchedProperty);
          return [summaryProperty.id, summaryProperty] as const;
        });
        const nextPropertyMap = Object.fromEntries(nextPropertyEntries) as Record<string, Property>;
        const nextPropertyOrder = nextPropertyEntries.map(([propertyId]) => propertyId);

        replaceProperties(nextPropertyMap, nextPropertyOrder);
        replacePortfolios(
          Object.fromEntries(
            (((portfolioResult.data as SupabasePortfolioRow[] | null) || []).map((row) => {
              const portfolio = mapSupabasePortfolioRowToRecord(row);
              return [portfolio.id, portfolio];
            }))
          ) as Record<string, Portfolio>
        );
        replacePortfolioMemberships(
          ((membershipResult.data as SupabasePortfolioPropertyRow[] | null) || []).map((row) => ({
            portfolioId: row.portfolio_id,
            propertyId: row.property_id
          }))
        );

        setLoadedSupabaseScopeKey(activeDataScopeKey);
        setWorkspaceSummaryLoadState('ready');
        setPropertySyncStatus(summaryRows.length > 0
          ? {
              tone: 'info',
              message: `Loaded ${summaryRows.length} saved propert${summaryRows.length === 1 ? 'y' : 'ies'}.`
            }
          : {
              tone: 'info',
              message: 'No saved properties found yet. Create one from the main property flow to persist it.'
            });
      } catch (error) {
        if (isCancelled || workspaceSummaryRequestRef.current !== requestId) return;

        setWorkspaceSummaryLoadState('error');
        setPropertySyncStatus({
          tone: 'error',
          message: `Workspace summary could not be fetched: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`
        });
      } finally {
        isLoadingSupabasePropertiesRef.current = false;
      }
    };

    void loadWorkspaceSummary();

    return () => {
      isCancelled = true;
    };
  }, [activeDataScopeKey, activeTeamId, isLoaded, loadedSupabaseScopeKey, properties, replacePortfolioMemberships, replacePortfolios, replaceProperties, user]);

  const shouldLoadDebtLayer = Boolean(
    user &&
    isLoaded &&
    activeTeamId &&
    (activeWorkspace === 'debt' || activeWorkspace === 'tax' || activePortfolioId || activePropertyId)
  );

  useEffect(() => {
    if (!shouldLoadDebtLayer || !user) return;
    if (debtLayerLoadState === 'ready' && loadedSupabaseScopeKey === activeDataScopeKey) return;

    let isCancelled = false;
    const requestId = debtLayerRequestRef.current + 1;
    debtLayerRequestRef.current = requestId;

    const loadDebtLayer = async () => {
      setDebtLayerLoadState('loading');

      try {
        const debtQuery = applyOwnershipScope(
          supabase.from('property_debts').select(SUPABASE_PROPERTY_DEBT_COLUMNS),
          user.uid,
          activeTeamId
        ).order('created_at', { ascending: true });

        const linkQuery = applyOwnershipScope(
          supabase.from('property_debt_links').select(SUPABASE_PROPERTY_DEBT_LINK_COLUMNS),
          user.uid,
          activeTeamId
        ).order('created_at', { ascending: true });

        const eventQuery = applyOwnershipScope(
          supabase.from('debt_events').select(SUPABASE_DEBT_EVENT_COLUMNS),
          user.uid,
          activeTeamId
        )
          .order('event_date', { ascending: true })
          .order('created_at', { ascending: true });

        const [debtResult, linkResult, eventResult] = await Promise.all([
          debtQuery,
          linkQuery,
          eventQuery
        ]);

        if (isCancelled || debtLayerRequestRef.current !== requestId) return;

        if (debtResult.error || linkResult.error || eventResult.error) {
          const message = debtResult.error?.message || linkResult.error?.message || eventResult.error?.message || 'Unknown fetch failure';
          setDebtLayerLoadState('error');
          setPropertySyncStatus({
            tone: 'error',
            message: `Debt workspace data could not be fetched: ${message}`
          });
          return;
        }

        replaceDebts(((debtResult.data as SupabasePropertyDebtRow[] | null) || []).map((row) => {
          const existingDebt = debts[row.id] || (Object.values(debts) as DebtRecord[]).find((debt: DebtRecord) => debt.supabaseId === row.id) || null;
          return mapSupabaseDebtRowToRecord(row, existingDebt);
        }));
        replaceDebtLinks(((linkResult.data as SupabasePropertyDebtLinkRow[] | null) || []).map((row) => {
          const existingLink = debtLinks.find((link: PropertyDebtLink) => link.id === row.id || (link.debtId === row.debt_id && link.propertyId === row.property_id)) || null;
          return mapSupabaseDebtLinkRowToRecord(row, existingLink);
        }));
        replaceDebtEvents(((eventResult.data as SupabaseDebtEventRow[] | null) || []).map((row) => mapSupabaseDebtEventRowToRecord(row)));
        setDebtLayerLoadState('ready');
      } catch (error) {
        if (isCancelled || debtLayerRequestRef.current !== requestId) return;

        setDebtLayerLoadState('error');
        setPropertySyncStatus({
          tone: 'error',
          message: `Debt workspace data could not be fetched: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`
        });
      }
    };

    void loadDebtLayer();

    return () => {
      isCancelled = true;
    };
  }, [activeDataScopeKey, activePortfolioId, activePropertyId, activeTeamId, activeWorkspace, debtLayerLoadState, isLoaded, loadedSupabaseScopeKey, replaceDebtEvents, replaceDebtLinks, replaceDebts, shouldLoadDebtLayer, user]);

  const refetchDebtLinks = useCallback(async () => {
    if (!user) return;

    debugPropertySync('Debt link refetch start', {
      authenticatedUserId: user.uid,
      query: {
        table: 'property_debt_links',
        team_id: activeTeamId,
        order_by: 'created_at.asc'
      }
    });

    try {
      const scopedQuery = applyOwnershipScope(
        supabase
          .from('property_debt_links')
          .select(SUPABASE_PROPERTY_DEBT_LINK_COLUMNS),
        user.uid,
        activeTeamId
      );
      const { data, error } = await scopedQuery.order('created_at', { ascending: true });

      debugPropertySync('Debt link refetch result', {
        authenticatedUserId: user.uid,
        returnedRowCount: data?.length ?? 0,
        returnedLinkIds: data?.map((row: any) => row.id) ?? [],
        error: describeSupabaseError(error)
      });

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Debt links could not be refreshed: ${error.message}`
        });
        return;
      }

      replaceDebtLinks(((data as SupabasePropertyDebtLinkRow[] | null) || []).map((row) => {
        const existingLink = debtLinks.find((link: PropertyDebtLink) => link.id === row.id || (link.debtId === row.debt_id && link.propertyId === row.property_id)) || null;
        return mapSupabaseDebtLinkRowToRecord(row, existingLink);
      }));
    } catch (error) {
      debugPropertySync('Debt link refetch exception', {
        authenticatedUserId: user.uid,
        error: error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack ?? null
            }
          : { raw: String(error) }
      });
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt links could not be refreshed: ${error instanceof Error ? error.message : 'Unknown fetch failure'}`
      });
    }
  }, [activeTeamId, replaceDebtLinks, user]);

  const handleUpsertDebtLink = useCallback(async (link: PropertyDebtLink) => {
    if (!user) return link.id;

    const debtRecords = Object.values(debts) as DebtRecord[];
    const linkedDebt = debts[link.debtId] || debtRecords.find((debt: DebtRecord) => debt.supabaseId === link.debtId) || null;
    const persistedDebtId = linkedDebt?.supabaseId || (isUuid(link.debtId) ? link.debtId : null);
    const persistedPropertyId = isUuid(link.propertyId) ? link.propertyId : resolveSupabasePropertyId(link.propertyId, properties);

    debugPropertySync('Debt link upsert start', {
      authenticatedUserId: user.uid,
      activeDebtId: link.debtId,
      persistedDebtId,
      propertyIdBeingLinked: link.propertyId,
      persistedPropertyId
    });

    if (!persistedDebtId || !persistedPropertyId) {
      setPropertySyncStatus({
        tone: 'error',
        message: 'Debt link save failed: the selected debt or property is not fully synced to Supabase yet.'
      });
      return null;
    }

    const normalizedLink: PropertyDebtLink = {
      ...link,
      debtId: persistedDebtId,
      propertyId: persistedPropertyId
    };
    const existingLink = debtLinks.find((existing: PropertyDebtLink) => existing.id === link.id || (existing.debtId === link.debtId && existing.propertyId === link.propertyId));
    const linksForDebt = [
      ...debtLinks.filter((candidate: PropertyDebtLink) => (
        candidate.debtId === persistedDebtId && candidate.id !== existingLink?.id
      )),
      normalizedLink
    ];
    const persistedAllocationPercent = normalizedLink.allocationMethod === 'manual'
      ? normalizedLink.allocationPercent
      : resolveDebtAllocationPercent(
          normalizedLink,
          linksForDebt,
          properties,
          undefined,
          linkedDebt || undefined,
          debts,
          debtEvents
        );
    const persistedLinkId = isUuid(existingLink?.id)
      ? existingLink?.id
      : isUuid(link.id)
        ? link.id
        : null;
    const payload = {
      ...buildOwnershipPayload(user.uid, activeTeamId),
      ...buildSupabasePropertyDebtLinkPayload({
        ...normalizedLink,
        allocationPercent: persistedAllocationPercent
      }),
      ...(persistedLinkId ? { id: persistedLinkId } : {})
    };

    const { data, error } = await supabase
      .from('property_debt_links')
      .upsert(payload, { onConflict: 'debt_id,property_id' })
      .select(SUPABASE_PROPERTY_DEBT_LINK_COLUMNS)
      .single();

    debugPropertySync('Debt link upsert result', {
      authenticatedUserId: user.uid,
      link,
      payload,
      responseData: data ?? null,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt link save failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return null;
    }

    upsertDebtLink(mapSupabaseDebtLinkRowToRecord(data as SupabasePropertyDebtLinkRow, normalizedLink));
    setPropertySyncStatus({
      tone: 'success',
      message: `Debt link saved to Supabase (${normalizedLink.allocationMethod === 'manual' ? `${persistedAllocationPercent.toFixed(2)}% manual allocation` : `${persistedAllocationPercent.toFixed(2)}% via ${normalizedLink.allocationMethod.replace(/_/g, ' ')}`}).`
    });
    await refetchDebtLinks();
    return data.id as string;
  }, [activeTeamId, debtLinks, debts, properties, refetchDebtLinks, upsertDebtLink, user]);

  const handleRemoveDebtLink = useCallback(async (linkId: string) => {
    const existingLink = debtLinks.find((link: PropertyDebtLink) => link.id === linkId) || null;
    removeDebtLink(linkId);

    if (!user || !existingLink) return;

    const scopedQuery = applyOwnershipScope(
      supabase
        .from('property_debt_links')
        .delete()
        .eq('id', existingLink.id),
      user.uid,
      activeTeamId
    );
    const { error } = await scopedQuery;

    debugPropertySync('Debt link delete result', {
      authenticatedUserId: user.uid,
      linkId,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt link delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }

    setPropertySyncStatus({
      tone: 'success',
      message: 'Debt link removed from Supabase.'
    });
    await refetchDebtLinks();
  }, [activeTeamId, debtLinks, refetchDebtLinks, removeDebtLink, user]);

  const handleSaveDebt = useCallback(async (debt: DebtRecord) => {
    const remotePropertyId = activeSupabasePropertyId || resolveSupabasePropertyId(activePropertyId ?? debt.propertyId ?? null, properties);
    const propertyScopedDebt: DebtRecord = {
      ...debt,
      propertyId: remotePropertyId ?? activePropertyId ?? debt.propertyId ?? null
    };

    if (!user) {
      saveDebt(propertyScopedDebt);
      if (propertyScopedDebt.propertyId) {
      }
      return propertyScopedDebt.id;
    }

    if (!remotePropertyId) {
      debugPropertySync('Debt insert blocked', {
        reason: 'No Supabase property id is available for the active property.',
        authenticatedUserId: user.uid,
        activePropertyId,
        debtLocalId: propertyScopedDebt.id,
        debtPropertyId: debt.propertyId ?? null
      });
      setPropertySyncStatus({
        tone: 'error',
        message: 'Debt saved locally, but Supabase insert was skipped because this property does not have a synced Supabase id yet.'
      });
      return null;
    }

    saveDebt(propertyScopedDebt);

    const payload = buildSupabasePropertyDebtPayload(remotePropertyId, propertyScopedDebt);
    const remoteDebtId = propertyScopedDebt.supabaseId ?? null;
    const payloadKeys = Object.keys(payload);
    const unexpectedPayloadKeys = payloadKeys.filter((key) => !SUPABASE_PROPERTY_DEBT_MUTATION_COLUMNS.includes(key as typeof SUPABASE_PROPERTY_DEBT_MUTATION_COLUMNS[number]));

    debugPropertySync(remoteDebtId ? 'Debt update start' : 'Debt insert start', {
      authenticatedUserId: user.uid,
      activePropertyId,
      remotePropertyId,
      remoteDebtId,
      payload,
      payloadKeys,
      unexpectedPayloadKeys
    });

    if (remoteDebtId) {
      const scopedQuery = applyOwnershipScope(
        supabase
          .from('property_debts')
          .update(payload)
          .eq('id', remoteDebtId)
          .eq('property_id', remotePropertyId)
          .select(SUPABASE_PROPERTY_DEBT_COLUMNS),
        user.uid,
        activeTeamId
      );
      const { data, error } = await scopedQuery.maybeSingle();

      debugPropertySync('Debt update result', {
        authenticatedUserId: user.uid,
        activePropertyId,
        remotePropertyId,
        remoteDebtId,
        responseData: data ?? null,
        error: describeSupabaseError(error)
      });

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Debt saved locally, but Supabase update failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
        return null;
      }

      if (data) {
        saveDebt({
          ...mapSupabaseDebtRowToRecord(data as SupabasePropertyDebtRow, propertyScopedDebt),
          id: data.id,
          supabaseId: data.id,
          propertyId: remotePropertyId
        });
        setPropertySyncStatus({
          tone: 'success',
          message: 'Debt updated in Supabase.'
        });
        await handleUpsertDebtLink({
          id: `property-debt-link:${remotePropertyId}:${data.id}`,
          debtId: data.id,
          propertyId: remotePropertyId,
          allocationPercent: 100,
          allocationMethod: 'manual'
        });
        if (propertyScopedDebt.id !== data.id) {
          deleteDebt(propertyScopedDebt.id);
        }
        return data.id;
      }
      return propertyScopedDebt.id;
    }

    const { data, error } = await supabase
      .from('property_debts')
      .insert({
        ...buildOwnershipPayload(user.uid, activeTeamId),
        ...payload
      })
      .select(SUPABASE_PROPERTY_DEBT_COLUMNS)
      .single();

    debugPropertySync('Debt insert result', {
      authenticatedUserId: user.uid,
      activePropertyId,
      remotePropertyId,
      responseData: data ?? null,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt saved locally, but Supabase insert failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return null;
    }

    saveDebt({
      ...mapSupabaseDebtRowToRecord(data as SupabasePropertyDebtRow, propertyScopedDebt),
      id: data.id,
      supabaseId: data.id,
      propertyId: remotePropertyId
    });
    setPropertySyncStatus({
      tone: 'success',
      message: 'Debt saved to Supabase.'
    });
    await handleUpsertDebtLink({
      id: `property-debt-link:${remotePropertyId}:${data.id}`,
      debtId: data.id,
      propertyId: remotePropertyId,
      allocationPercent: 100,
      allocationMethod: 'manual'
    });
    if (propertyScopedDebt.id !== data.id) {
      deleteDebt(propertyScopedDebt.id);
    }
    return data.id;
  }, [activePropertyId, activeSupabasePropertyId, activeTeamId, deleteDebt, handleUpsertDebtLink, properties, saveDebt, user]);

  const handleDeleteDebt = useCallback(async (debtId: string) => {
    const debt = debts[debtId];
    deleteDebt(debtId);

    if (!user || !debt?.supabaseId) return;

    const remotePropertyId = resolveSupabasePropertyId(debt.propertyId ?? null, properties);
    if (!remotePropertyId) return;

    const scopedQuery = applyOwnershipScope(
      supabase
        .from('property_debts')
        .delete()
        .eq('id', debt.supabaseId)
        .eq('property_id', remotePropertyId),
      user.uid,
      activeTeamId
    );
    const { error } = await scopedQuery;

    debugPropertySync('Debt delete result', {
      authenticatedUserId: user.uid,
      debtId,
      remoteDebtId: debt.supabaseId,
      remotePropertyId,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt deleted locally, but Supabase delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }
    setPropertySyncStatus({
      tone: 'success',
      message: 'Debt deleted from Supabase.'
    });
  }, [activeTeamId, debts, deleteDebt, properties, user]);

  const handleSaveDebtEvent = useCallback(async (event: DebtEvent) => {
    const existingEvent = debtEvents.find((entry: DebtEvent) => entry.id === event.id) || null;
    saveDebtEvent(event);

    if (!user) return;

    const remoteDebtId = debts[event.debtId]?.supabaseId || (isUuid(event.debtId) ? event.debtId : null);
    if (!remoteDebtId) {
      setPropertySyncStatus({
        tone: 'info',
        message: 'Debt event saved locally, but Supabase insert was skipped because this debt does not have a synced Supabase id yet.'
      });
      return;
    }

    const payload = {
      debt_id: remoteDebtId,
      event_date: event.date || new Date().toISOString().slice(0, 10),
      event_type: event.type || 'extra_principal_payment',
      amount: normalizeSupabaseNumber(event.amount),
      notes: event.notes || null
    };

    const remoteEventId = existingEvent && isUuid(existingEvent.id) ? existingEvent.id : null;
    if (remoteEventId) {
      const scopedQuery = applyOwnershipScope(
        supabase
          .from('debt_events')
          .update(payload)
          .eq('id', remoteEventId)
          .select(SUPABASE_DEBT_EVENT_COLUMNS),
        user.uid,
        activeTeamId
      );
      const { data, error } = await scopedQuery.single();

      debugPropertySync('Debt event update result', {
        authenticatedUserId: user.uid,
        remoteDebtId,
        remoteEventId,
        error: describeSupabaseError(error)
      });

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Debt event saved locally, but Supabase update failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
        return;
      }

      saveDebtEvent(mapSupabaseDebtEventRowToRecord(data as SupabaseDebtEventRow));
      setPropertySyncStatus({
        tone: 'success',
        message: 'Debt event updated in Supabase.'
      });
      return;
    }

    const { data, error } = await supabase
      .from('debt_events')
      .insert({
        ...buildOwnershipPayload(user.uid, activeTeamId),
        ...payload
      })
      .select(SUPABASE_DEBT_EVENT_COLUMNS)
      .single();

    debugPropertySync('Debt event insert result', {
      authenticatedUserId: user.uid,
      remoteDebtId,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt event saved locally, but Supabase insert failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }

    saveDebtEvent(mapSupabaseDebtEventRowToRecord(data as SupabaseDebtEventRow));
    if (event.id !== data.id) {
      deleteDebtEvent(event.id);
    }
    setPropertySyncStatus({
      tone: 'success',
      message: 'Debt event saved to Supabase.'
    });
  }, [activeTeamId, debtEvents, debts, deleteDebtEvent, saveDebtEvent, user]);

  const handleDeleteDebtEvent = useCallback(async (eventId: string) => {
    const event = debtEvents.find((entry: DebtEvent) => entry.id === eventId) || null;
    deleteDebtEvent(eventId);

    if (!user || !event || !isUuid(event.id)) return;

    const scopedQuery = applyOwnershipScope(
      supabase
        .from('debt_events')
        .delete()
        .eq('id', event.id),
      user.uid,
      activeTeamId
    );
    const { error } = await scopedQuery;

    debugPropertySync('Debt event delete result', {
      authenticatedUserId: user.uid,
      eventId,
      error: describeSupabaseError(error)
    });

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Debt event deleted locally, but Supabase delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }

    setPropertySyncStatus({
      tone: 'success',
      message: 'Debt event deleted from Supabase.'
    });
  }, [activeTeamId, debtEvents, deleteDebtEvent, user]);

  const handleSaveProperty = useCallback(async () => {
    if (isSavingProperty) return;

    setIsSavingProperty(true);
    await yieldToBrowser();

    try {
      const localPropertyId = activePropertyId || crypto.randomUUID();
      const safeName = propertyName.trim() || 'Untitled Property';
      const existing = activeExistingProperty;
      const existingSupabaseId = existing?.supabaseId ?? null;
      const baselineChanged = !existing || existingPropertyBaselineSignature !== currentPropertyBaselineSignature;
      const nextAuditLog = baselineChanged
        ? [{
            id: crypto.randomUUID(),
            propertyId: localPropertyId,
            kind: 'baseline_edit' as const,
            title: existing ? 'Baseline model updated' : 'Baseline model created',
            detail: existing
              ? 'Core property inputs, baseline operating assumptions, or the current timeline changed and the baseline model was recalculated.'
              : 'The baseline model was created and is now the source of truth for future events and scenarios.',
            createdAt: Date.now()
          }, ...auditLog]
        : auditLog;

      let nextSupabaseId = existingSupabaseId;
      let supabaseSaveSucceeded = false;
      let nextInputs = inputs;
      let nextBaseYear = baseYear;
      let nextHistoricalActuals = historicalActuals;
      let nextRiskProfile = riskProfile;
      let nextModelEvents = modelEvents;
      let nextSavedScenarios = savedScenarios;
      let nextAuditLogState = nextAuditLog;

      if (user) {
      const payload = buildSupabasePropertyPayload(
        Math.max(1, (activePropertyId ? propertyOrder.indexOf(activePropertyId) : propertyOrder.length) + 1),
        safeName,
        propertyAddress,
        propertyOwnershipEntity,
        nextBaseYear,
        inputs,
        nextHistoricalActuals,
        nextRiskProfile,
        nextModelEvents,
        nextSavedScenarios,
        nextAuditLogState
      );
      debugPropertySync('Save start', {
        openPropertyId: activePropertyId,
        localPropertyId,
        existingProperty: existing ? {
          id: existing.id,
          supabaseId: existing.supabaseId ?? null,
          name: existing.name,
          address: existing.address
        } : null,
        eqIdForUpdate: existingSupabaseId,
        eqTeamIdForUpdate: activeTeamId,
        authenticatedUserId: user.uid,
        payload
      });

      if (existingSupabaseId) {
        const existingRemoteRecord = await selectSingleSupabaseProperty(existingSupabaseId, user.uid, activeTeamId);

        if (existingRemoteRecord.error) {
          setPropertySyncStatus({
            tone: 'error',
            message: `Property saved locally, but Supabase lookup failed before update: ${existingRemoteRecord.error.message}`
          });
        } else if (!existingRemoteRecord.data) {
          debugPropertySync('Update fallback to insert', {
            reason: 'lookup returned no row',
            eqIdForUpdate: existingSupabaseId,
            eqTeamIdForUpdate: activeTeamId
          });
          nextSupabaseId = null;
        } else {
          debugPropertySync('Update start', {
            propertyId: existing?.id ?? null,
            propertySupabaseId: existingSupabaseId,
            authenticatedUserId: user.uid,
            eqIdForUpdate: existingSupabaseId,
            eqTeamIdForUpdate: activeTeamId,
            payload
          });
          const scopedQuery = applyOwnershipScope(
            supabase
              .from('properties')
              .update(payload)
              .eq('id', existingSupabaseId)
              .select(SUPABASE_PROPERTY_COLUMNS),
            user.uid,
            activeTeamId
          );
          const { data, error } = await scopedQuery;

          const updatedRows = (data as SupabasePropertyRow[] | null) ?? null;
          debugPropertySync('Update result', {
            eqIdForUpdate: existingSupabaseId,
            eqTeamIdForUpdate: activeTeamId,
            returnedRowCount: updatedRows?.length ?? 0,
            returnedRowIds: updatedRows?.map((row) => row.id) ?? [],
            error: error?.message ?? null
          });

          if (error) {
            setPropertySyncStatus({
              tone: 'error',
              message: `Property saved locally, but Supabase update failed: ${error.message}`
            });
          } else if (!updatedRows || updatedRows.length !== 1) {
            setPropertySyncStatus({
              tone: 'error',
              message: `Property saved locally, but Supabase update returned ${updatedRows?.length ?? 0} matching rows instead of 1.`
            });
          } else {
            nextSupabaseId = updatedRows[0].id;
            nextInputs = mergeSupabaseInputs(updatedRows[0], inputs);
            const hydratedRemote = getHydratedPropertyDetails(updatedRows[0], existing);
            nextBaseYear = hydratedRemote.baseYear;
            nextHistoricalActuals = hydratedRemote.historicalActuals;
            nextRiskProfile = hydratedRemote.riskProfile;
            nextModelEvents = hydratedRemote.modelEvents;
            nextSavedScenarios = hydratedRemote.savedScenarios;
            nextAuditLogState = hydratedRemote.auditLog;
            supabaseSaveSucceeded = true;
          }
        }
      }

      if (!nextSupabaseId) {
        debugPropertySync('Insert start', {
          authenticatedUserId: user.uid,
          payload
        });
        const { data, error } = await supabase
          .from('properties')
          .insert({
            ...buildOwnershipPayload(user.uid, activeTeamId),
            ...payload
          })
          .select(SUPABASE_PROPERTY_COLUMNS)
          .single();

        debugPropertySync('Insert result', {
          authenticatedUserId: user.uid,
          returnedRowId: data?.id ?? null,
          returnedUserId: data?.user_id ?? null,
          error: error?.message ?? null
        });

        if (error) {
          setPropertySyncStatus({
            tone: 'error',
            message: `Property saved locally, but Supabase insert failed: ${error.message}`
          });
        } else {
          nextSupabaseId = data.id;
          nextInputs = mergeSupabaseInputs(data as SupabasePropertyRow, inputs);
          const hydratedRemote = getHydratedPropertyDetails(data as SupabasePropertyRow, existing);
          nextBaseYear = hydratedRemote.baseYear;
          nextHistoricalActuals = hydratedRemote.historicalActuals;
          nextRiskProfile = hydratedRemote.riskProfile;
          nextModelEvents = hydratedRemote.modelEvents;
          nextSavedScenarios = hydratedRemote.savedScenarios;
          nextAuditLogState = hydratedRemote.auditLog;
          supabaseSaveSucceeded = true;
        }
      }
      }

      const resolvedPropertyId = nextSupabaseId || localPropertyId;
      debugPropertySync('Save resolved ids', {
        openPropertyId: activePropertyId,
        localPropertyId,
        nextSupabaseId,
        resolvedPropertyId
      });

      saveProperty(
        resolvedPropertyId,
        safeName,
        propertyAddress,
        propertyOwnershipEntity,
        nextBaseYear,
        nextInputs,
        nextHistoricalActuals,
        nextRiskProfile,
        nextModelEvents,
        nextSavedScenarios,
        nextAuditLogState,
        modelResult.metrics,
        { supabaseId: nextSupabaseId, teamId: activeTeamId }
      );

      if (supabaseSaveSucceeded) {
        setPropertySyncStatus({
          tone: 'success',
          message: existingSupabaseId
            ? 'Property updated in Supabase and the local app state.'
            : 'Property created in Supabase and added to your app workspace.'
        });
      }

      setAuditLog(nextAuditLogState);
      setActivePropertyId(resolvedPropertyId);
      setShowLanding(false);
      setActiveWorkspace('property');
      setPropertyViewMode('dashboard');
      setActiveTab('dashboard');
      setLastSavedAt(Date.now());
      updateBrowserPath(`/property/${resolvedPropertyId}`);
    } finally {
      setIsSavingProperty(false);
    }
  }, [activeExistingProperty, activePropertyId, activeTeamId, auditLog, currentPropertyBaselineSignature, existingPropertyBaselineSignature, baseYear, historicalActuals, inputs, isSavingProperty, modelEvents, modelResult.metrics, propertyAddress, propertyName, propertyOrder, riskProfile, saveProperty, savedScenarios, updateBrowserPath, user]);

  const handleSaveModelEvent = useCallback((event: PropertyModelEvent) => {
    const propertyId = activePropertyId || event.propertyId || 'unsaved-property';
    const existingEvent = modelEvents.find((existing: PropertyModelEvent) => existing.id === event.id);
    const normalizedEvent: PropertyModelEvent = {
      ...event,
      propertyId,
      sortOrder: event.sortOrder ?? existingEvent?.sortOrder ?? modelEvents.length,
      createdAt: event.createdAt || Date.now(),
      notes: event.notes || '',
      status: event.status || 'active'
    };
    const nextEvents = [...modelEvents.filter((existing: PropertyModelEvent) => existing.id !== normalizedEvent.id), normalizedEvent]
      .sort((left: PropertyModelEvent, right: PropertyModelEvent) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
      .map((entry: PropertyModelEvent, index: number) => ({ ...entry, sortOrder: index }));
    const auditEntry = {
      id: crypto.randomUUID(),
      propertyId,
      kind: existingEvent ? 'event_updated' as const : 'event_created' as const,
      title: `${existingEvent ? 'Event updated' : 'Event saved'}: ${normalizedEvent.name}`,
      detail: `${normalizedEvent.name} is now modeled as a ${normalizedEvent.type.replace('_', ' ')} ${normalizedEvent.category} event${existingEvent ? ', and the timeline record was updated.' : '.'}`,
      createdAt: Date.now()
    };
    const nextAuditLog = [auditEntry, ...auditLog];
    setModelEvents(nextEvents);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextModelEvents: nextEvents, nextAuditLog });
  }, [activePropertyId, auditLog, modelEvents, persistPropertyState]);

  const handleDeleteModelEvent = useCallback((eventId: string) => {
    const targetEvent = modelEvents.find((event: PropertyModelEvent) => event.id === eventId);
    if (!targetEvent) return;
    const nextEvents = modelEvents
      .filter((event: PropertyModelEvent) => event.id !== eventId)
      .map((event: PropertyModelEvent, index: number) => ({ ...event, sortOrder: index }));
    const nextAuditLog = [{
      id: crypto.randomUUID(),
      propertyId: activePropertyId || targetEvent.propertyId || 'unsaved-property',
      kind: 'event_deleted' as const,
      title: `Event deleted: ${targetEvent.name}`,
      detail: 'This modeled event was removed from the property timeline and no longer affects active projections.',
      createdAt: Date.now()
    }, ...auditLog];
    setModelEvents(nextEvents);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextModelEvents: nextEvents, nextAuditLog });
  }, [activePropertyId, auditLog, modelEvents, persistPropertyState]);

  const handleReorderModelEvent = useCallback((eventId: string, direction: 'up' | 'down') => {
    const activeEvents = modelEvents.filter((event: PropertyModelEvent) => event.status === 'active');
    const currentIndex = activeEvents.findIndex((event: PropertyModelEvent) => event.id === eventId);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= activeEvents.length) return;
    const reordered = [...activeEvents];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    const reorderedIds = reordered.map((event: PropertyModelEvent) => event.id);
    const nextEvents = modelEvents
      .slice()
      .sort((left: PropertyModelEvent, right: PropertyModelEvent) => {
        const leftIndex = reorderedIds.indexOf(left.id);
        const rightIndex = reorderedIds.indexOf(right.id);
        if (leftIndex === -1 && rightIndex === -1) return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      })
      .map((event: PropertyModelEvent, index: number) => ({ ...event, sortOrder: index }));
    const movedEvent = modelEvents.find((event: PropertyModelEvent) => event.id === eventId);
    const nextAuditLog = movedEvent ? [{
      id: crypto.randomUUID(),
      propertyId: activePropertyId || movedEvent.propertyId || 'unsaved-property',
      kind: 'event_reordered' as const,
      title: `Event reordered: ${movedEvent.name}`,
      detail: 'The event timeline order was updated for easier tracking in the chronological list.',
      createdAt: Date.now()
    }, ...auditLog] : auditLog;
    setModelEvents(nextEvents);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextModelEvents: nextEvents, nextAuditLog });
  }, [activePropertyId, auditLog, modelEvents, persistPropertyState]);

  const handleSaveScenario = useCallback((scenario: SavedModelScenario) => {
    const propertyId = activePropertyId || scenario.propertyId || 'unsaved-property';
    const normalizedScenario: SavedModelScenario = {
      ...scenario,
      propertyId,
      items: Array.isArray(scenario.items) ? scenario.items : [],
      summary: null,
      createdAt: scenario.createdAt || Date.now(),
      lastRunAt: null
    };
    const nextScenarios = [normalizedScenario, ...savedScenarios.filter((existing: SavedModelScenario) => existing.id !== normalizedScenario.id)];
    const nextAuditLog = [{
      id: crypto.randomUUID(),
      propertyId,
      kind: 'scenario_saved' as const,
      title: `Scenario saved: ${normalizedScenario.name}`,
      detail: `This what-if scenario stores ${normalizedScenario.items.length} line item${normalizedScenario.items.length === 1 ? '' : 's'} separately from the baseline and can be rerun later.`,
      createdAt: Date.now()
    }, ...auditLog];
    setSavedScenarios(nextScenarios);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextSavedScenarios: nextScenarios, nextAuditLog });
  }, [activePropertyId, auditLog, persistPropertyState, savedScenarios]);

  const handleDeleteScenario = useCallback((scenarioId: string) => {
    const targetScenario = savedScenarios.find((scenario: SavedModelScenario) => scenario.id === scenarioId);
    if (!targetScenario) return;
    const nextScenarios = savedScenarios.filter((scenario: SavedModelScenario) => scenario.id !== scenarioId);
    const nextAuditLog = [{
      id: crypto.randomUUID(),
      propertyId: activePropertyId || targetScenario.propertyId || 'unsaved-property',
      kind: 'scenario_deleted' as const,
      title: `Scenario deleted: ${targetScenario.name}`,
      detail: 'The saved what-if scenario was removed without changing the baseline model.',
      createdAt: Date.now()
    }, ...auditLog];
    setSavedScenarios(nextScenarios);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextSavedScenarios: nextScenarios, nextAuditLog });
  }, [activePropertyId, auditLog, persistPropertyState, savedScenarios]);

  const handleRunScenario = useCallback((scenarioId: string) => {
    const targetScenario = savedScenarios.find((scenario: SavedModelScenario) => scenario.id === scenarioId);
    if (!targetScenario) return;
    const nextScenarios = savedScenarios.map((scenario: SavedModelScenario) => scenario.id === scenarioId ? { ...scenario, lastRunAt: Date.now() } : scenario);
    const nextAuditLog = [{
      id: crypto.randomUUID(),
      propertyId: activePropertyId || targetScenario.propertyId || 'unsaved-property',
      kind: 'scenario_ran' as const,
      title: `Scenario rerun: ${targetScenario.name}`,
      detail: 'A saved what-if scenario was rerun without overwriting the baseline property model.',
      createdAt: Date.now()
    }, ...auditLog];
    setSavedScenarios(nextScenarios);
    setAuditLog(nextAuditLog);
    persistPropertyState({ nextSavedScenarios: nextScenarios, nextAuditLog });
  }, [activePropertyId, auditLog, persistPropertyState, savedScenarios]);

  useEffect(() => {
    if (activeWorkspace !== 'property' || propertyViewMode !== 'dashboard' || !activePropertyId) return;
    const property = properties[activePropertyId];
    if (!property || property.detailLevel !== 'full') return;

    setPropertyName(property.name);
    setPropertyAddress(property.address || '');
    setPropertyOwnershipEntity(property.ownershipEntity || '');
    setBaseYear(property.baseYear || INITIAL_BASE_YEAR);
    setInputs(property.currentYearInputs || property.inputs);
    setHistoricalActuals(property.historicalActuals || []);
    setRiskProfile(property.riskProfile || createDefaultRiskProfile());
    setModelEvents(property.modelEvents || []);
    setSavedScenarios(property.savedScenarios || []);
    setAuditLog(property.auditLog || []);
  }, [activePropertyId, activeWorkspace, properties, propertyViewMode]);

  const loadProperty = useCallback((id: string) => {
    const property = properties[id];
    if (!property) return;
    setActivePropertyId(id);
    setActivePortfolioId(null);
    setPropertyName(property.name);
    setPropertyAddress(property.address || '');
    setPropertyOwnershipEntity(property.ownershipEntity || '');
    setBaseYear(property.baseYear || INITIAL_BASE_YEAR);
    setInputs(property.currentYearInputs || property.inputs);
    setHistoricalActuals(property.historicalActuals || []);
    setRiskProfile(property.riskProfile || createDefaultRiskProfile());
    setModelEvents(property.modelEvents || []);
    setSavedScenarios(property.savedScenarios || []);
    setAuditLog(property.auditLog || []);
    setShowLanding(false);
    setActiveWorkspace('property');
    setPropertyViewMode('dashboard');
    setActiveTab('dashboard');
    updateBrowserPath(`/property/${id}`);
    if (property.detailLevel !== 'full') {
      void hydratePropertiesByIds([id]);
    }
  }, [hydratePropertiesByIds, properties, updateBrowserPath]);

  const handleSelectPortfolio = useCallback((id: string) => {
    setActivePortfolioId(id);
    setActivePropertyId(null);
    setShowLanding(false);
    setActiveWorkspace('portfolio');
    updateBrowserPath(`/portfolio/${id}`);
    const portfolio = portfolios[id];
    if (portfolio?.propertyIds?.length) {
      void hydratePropertiesByIds(portfolio.propertyIds);
    }
  }, [hydratePropertiesByIds, portfolios, updateBrowserPath]);

  const handleNewProperty = useCallback(() => {
    setActivePropertyId(null);
    setActivePortfolioId(null);
    setPropertyName('New Asset Opportunity');
    setPropertyAddress('');
    setPropertyOwnershipEntity('');
    setBaseYear(INITIAL_BASE_YEAR);
    setInputs(createInitialInputs());
    setHistoricalActuals([]);
    setRiskProfile(createDefaultRiskProfile());
    setModelEvents([]);
    setSavedScenarios([]);
    setAuditLog([]);
    setShowLanding(false);
    setActiveWorkspace('property');
    setPropertyViewMode('create-core');
    setActiveTab('dashboard');
    setLastSavedAt(null);
    updateBrowserPath('/property/new');
  }, [updateBrowserPath]);

  const handleContinuePropertyIntake = useCallback(() => {
    // In edit mode, keep step-to-step navigation from feeling lossy by
    // persisting the current draft before moving forward.
    if (activePropertyId && propertyViewMode.startsWith('edit-')) {
      persistPropertyState();
    }

    if (propertyViewMode === 'edit-core' && activePropertyId) {
      setPropertyViewMode('edit-operations');
      updateBrowserPath(`/property/${activePropertyId}/edit/operations`);
      return;
    }
    if (propertyViewMode === 'edit-operations' && activePropertyId) {
      setPropertyViewMode('edit-forecast');
      updateBrowserPath(`/property/${activePropertyId}/edit/forecast`);
      return;
    }
    if (propertyViewMode === 'create-operations') {
      setPropertyViewMode('create-forecast');
      updateBrowserPath('/property/new/forecast');
      return;
    }

    setPropertyViewMode('create-operations');
    updateBrowserPath('/property/new/operations');
  }, [activePropertyId, persistPropertyState, propertyViewMode, updateBrowserPath]);

  const handleBackPropertyIntake = useCallback(() => {
    // Mirror the forward-step behavior so edits like ownership entity / LLC
    // stay attached to the property record while moving around the flow.
    if (activePropertyId && propertyViewMode.startsWith('edit-')) {
      persistPropertyState();
    }

    if (propertyViewMode === 'edit-forecast' && activePropertyId) {
      setPropertyViewMode('edit-operations');
      updateBrowserPath(`/property/${activePropertyId}/edit/operations`);
      return;
    }
    if (propertyViewMode === 'edit-operations' && activePropertyId) {
      setPropertyViewMode('edit-core');
      updateBrowserPath(`/property/${activePropertyId}/edit`);
      return;
    }
    if (propertyViewMode === 'create-forecast') {
      setPropertyViewMode('create-operations');
      updateBrowserPath('/property/new/operations');
      return;
    }
    if (propertyViewMode === 'create-operations') {
      setPropertyViewMode('create-core');
      updateBrowserPath('/property/new');
      return;
    }

    setPropertyViewMode('create-core');
    updateBrowserPath('/property/new');
  }, [activePropertyId, persistPropertyState, propertyViewMode, updateBrowserPath]);

  const handleEditProperty = useCallback(() => {
    if (!activePropertyId) return;
    const property = properties[activePropertyId];
    if (!property) return;
    setPropertyName(property.name);
    setPropertyAddress(property.address || '');
    setPropertyOwnershipEntity(property.ownershipEntity || '');
    setBaseYear(property.baseYear || INITIAL_BASE_YEAR);
    setInputs(property.currentYearInputs || property.inputs);
    setHistoricalActuals(property.historicalActuals || []);
    setRiskProfile(property.riskProfile || createDefaultRiskProfile());
    setModelEvents(property.modelEvents || []);
    setSavedScenarios(property.savedScenarios || []);
    setAuditLog(property.auditLog || []);
    setActiveWorkspace('property');
    setPropertyViewMode('edit-core');
    updateBrowserPath(`/property/${activePropertyId}/edit`);
  }, [activePropertyId, properties, updateBrowserPath]);

  const handleSelectDebtWorkspace = useCallback(() => {
    const fallbackSyncedProperty = (Object.values(properties) as Array<(typeof properties)[string]>)
      .find((property) => Boolean(property.supabaseId)) || null;
    const nextActivePropertyId = activePropertyId || fallbackSyncedProperty?.id || null;
    const nextActiveSupabasePropertyId = activePropertyId
      ? activeSupabasePropertyId
      : fallbackSyncedProperty?.supabaseId || null;

    if (!nextActivePropertyId) {
      setPropertySyncStatus({
        tone: 'info',
        message: 'Select a property first, then save it to Supabase before opening the debt workspace.'
      });
      return;
    }

    if (!nextActiveSupabasePropertyId) {
      setPropertySyncStatus({
        tone: 'info',
        message: 'Save this property to Supabase before creating debt so debt rows have a valid property id.'
      });
      return;
    }

    if (!activePropertyId && nextActivePropertyId) {
      setActivePropertyId(nextActivePropertyId);
    }
    setActivePortfolioId(null);
    setShowLanding(false);
    setPropertySyncStatus(null);
    setActiveWorkspace('debt');
    updateBrowserPath('/debt');
  }, [activePropertyId, activeSupabasePropertyId, properties, updateBrowserPath]);

  const handleSelectDebtFromSidebar = useCallback((debtId: string) => {
    const debt = debts[debtId];
    if (!debt) return;

    const resolvedProperty = debt.propertyId
      ? properties[debt.propertyId] || (Object.values(properties) as Array<(typeof properties)[string]>).find((property) => property.supabaseId === debt.propertyId) || null
      : null;

    if (resolvedProperty?.id) {
      setActivePropertyId(resolvedProperty.id);
    }
    setSelectedDebtId(debtId);
    setActivePortfolioId(null);
    setShowLanding(false);
    setPropertySyncStatus(null);
    setActiveWorkspace('debt');
    updateBrowserPath('/debt');
  }, [debts, properties, updateBrowserPath]);

  const handleSelectTaxWorkspace = useCallback(() => {
    setActivePortfolioId(null);
    setShowLanding(false);
    setPropertySyncStatus(null);
    setActiveWorkspace('tax');
    updateBrowserPath('/tax-readiness');
  }, [updateBrowserPath]);

  useEffect(() => {
    if (activeWorkspace !== 'tax') return;
    const summaryOnlyPropertyIds = propertyDashboardProperties
      .filter((property: Property) => property.detailLevel !== 'full')
      .map((property: Property) => property.id);

    if (summaryOnlyPropertyIds.length > 0) {
      void hydratePropertiesByIds(summaryOnlyPropertyIds);
    }
  }, [activeWorkspace, hydratePropertiesByIds, propertyDashboardProperties]);

  const handleOpenPropertyDashboard = useCallback(() => {
    setActivePropertyId(null);
    setActivePortfolioId(null);
    setShowLanding(false);
    setPropertySyncStatus(null);
    setActiveWorkspace('property');
    setPropertyViewMode('dashboard');
    setActiveTab('dashboard');
    updateBrowserPath('/properties');
  }, [updateBrowserPath]);

  const handleLogout = useCallback(async () => {
    setPropertySyncStatus(null);
    setActivePortfolioId(null);
    setActivePropertyId(null);
    setActiveWorkspace('property');
    setShowLanding(true);
    updateBrowserPath('/', true);
    try {
      await logout();
    } catch (error) {
      debugPropertySync('Logout failure', {
        error: error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack ?? null
            }
          : { raw: String(error) }
      });
    }
  }, [logout, updateBrowserPath]);

  const handleStartAnalysis = useCallback(() => {
    setShowLanding(false);
    if (propertyOrder.length > 0) {
      loadProperty(propertyOrder[0]);
    } else {
      handleNewProperty();
    }
  }, [handleNewProperty, loadProperty, propertyOrder]);

  const handleAddTeamMember = useCallback(async (email: string) => {
    if (!user || !activeTeamId) return;
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;

    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select(SUPABASE_USER_PROFILE_COLUMNS)
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (profileError) {
        setTeamStatus(`Could not look up that user: ${profileError.message}`);
        return;
      }

      if (!profile) {
        setTeamStatus('That user needs to sign up first before you can add them to the team.');
        return;
      }

      const existingMember = teamMembers.find((member: TeamMember) => member.userId === profile.id);
      if (existingMember) {
        setTeamStatus(`${profile.email || 'That user'} is already on this team.`);
        return;
      }

      const { error: membershipError } = await supabase
        .from('team_memberships')
        .insert({
          team_id: activeTeamId,
          user_id: profile.id,
          role: 'editor'
        });

      if (membershipError) {
        setTeamStatus(`Could not add team member: ${membershipError.message}`);
        return;
      }

      setTeamStatus(`${profile.email || 'Team member'} added successfully.`);
      const { data: refreshedMemberships } = await supabase
        .from('team_memberships')
        .select(SUPABASE_TEAM_MEMBERSHIP_COLUMNS)
        .eq('team_id', activeTeamId)
        .order('created_at', { ascending: true });
      const profileIds = (((refreshedMemberships as SupabaseTeamMembershipRow[] | null) || []).map((membership) => membership.user_id));
      let refreshedProfiles: SupabaseUserProfileRow[] | null = [];
      if (profileIds.length > 0) {
        const { data: profileRows, error: refreshedProfileError } = await supabase
          .from('user_profiles')
          .select(SUPABASE_USER_PROFILE_COLUMNS)
          .in('id', profileIds);
        if (refreshedProfileError) {
          setTeamStatus(`Team member added, but profiles could not be refreshed: ${refreshedProfileError.message}`);
          return;
        }
        refreshedProfiles = (profileRows as SupabaseUserProfileRow[] | null) || [];
      }
      const profileMap = Object.fromEntries(
        (((refreshedProfiles as SupabaseUserProfileRow[] | null) || []).map((row) => [row.id, row]))
      ) as Record<string, SupabaseUserProfileRow>;
      setTeamMembers((((refreshedMemberships as SupabaseTeamMembershipRow[] | null) || []).map((membership) => mapSupabaseTeamMembershipToMember(membership, profileMap))));
    } catch (error) {
      setTeamStatus(`Could not add team member: ${error instanceof Error ? error.message : 'Unknown failure'}`);
    }
  }, [activeTeamId, teamMembers, user]);

  const handleRemoveTeamMember = useCallback(async (member: TeamMember) => {
    if (!user || !activeTeamId) return;

    if (member.userId === user.uid) {
      setTeamStatus('Use a separate ownership transfer flow before removing your own access.');
      return;
    }

    const ownerCount = teamMembers.filter((teamMember: TeamMember) => teamMember.role === 'owner').length;
    if (member.role === 'owner' && ownerCount <= 1) {
      setTeamStatus('You cannot remove the last owner from this workspace.');
      return;
    }

    const memberLabel = member.email || member.displayName || 'That member';

    try {
      const { data: deletedMemberships, error: membershipError } = await supabase
        .from('team_memberships')
        .delete()
        .eq('team_id', activeTeamId)
        .eq('user_id', member.userId)
        .select('id');

      if (membershipError) {
        setTeamStatus(`Could not remove team member: ${membershipError.message}`);
        return;
      }

      if (!deletedMemberships || deletedMemberships.length === 0) {
        setTeamStatus('Supabase did not remove that member. Check team permissions and try again.');
        return;
      }

      setTeamMembers((currentMembers: TeamMember[]) => currentMembers.filter((currentMember: TeamMember) => currentMember.id !== member.id));
      setTeamStatus(`${memberLabel} removed from this workspace.`);
    } catch (error) {
      setTeamStatus(`Could not remove team member: ${error instanceof Error ? error.message : 'Unknown failure'}`);
    }
  }, [activeTeamId, teamMembers, user]);

  const handleCreatePortfolio = useCallback(async () => {
    const name = prompt('Portfolio Name:', 'Core Growth Portfolio');
    if (!name) return;

    let id = createPortfolio(name);

    if (user) {
      const { data, error } = await supabase
        .from('portfolios')
        .insert({
          ...buildOwnershipPayload(user.uid, activeTeamId),
          name: name.trim() || 'Untitled Portfolio'
        })
        .select(SUPABASE_PORTFOLIO_COLUMNS)
        .single();

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Portfolio created locally, but Supabase insert failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
      } else {
        const remotePortfolio = mapSupabasePortfolioRowToRecord(data as SupabasePortfolioRow);
        deletePortfolio(id);
        replacePortfolios({
          ...portfolios,
          [remotePortfolio.id]: remotePortfolio
        });
        id = remotePortfolio.id;
        setPropertySyncStatus({
          tone: 'success',
          message: 'Portfolio created in Supabase.'
        });
      }
    }

    setActivePortfolioId(id);
    setActivePropertyId(null);
    setShowLanding(false);
    setActiveWorkspace('portfolio');
    updateBrowserPath(`/portfolio/${id}`);
  }, [activeTeamId, createPortfolio, deletePortfolio, portfolios, replacePortfolios, updateBrowserPath, user]);

  const handleReorderProperties = useCallback((newOrder: string[]) => {
    reorderProperties(newOrder);

    if (!user) return;

    const syncedProperties = newOrder
      .map((propertyId, index) => {
        const remotePropertyId = resolveSupabasePropertyId(propertyId, properties);
        return remotePropertyId ? {
          id: remotePropertyId,
          display_order: index + 1
        } : null;
      })
      .filter(Boolean) as Array<{ id: string; display_order: number }>;

    if (syncedProperties.length === 0) return;

    void (async () => {
      const { error } = await supabase
        .from('properties')
        .upsert(
          syncedProperties.map((property) => ({
            id: property.id,
            ...buildOwnershipPayload(user.uid, activeTeamId),
            display_order: property.display_order
          })),
          { onConflict: 'id' }
        );

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Property order saved locally, but Supabase sync failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
        return;
      }

      setPropertySyncStatus({
        tone: 'success',
        message: 'Property order saved to Supabase.'
      });
    })();
  }, [activeTeamId, properties, reorderProperties, user]);

  const handleViewPortfolio = useCallback(() => {
    const firstPortfolio = Object.values(portfolios)[0] as any;
    if (firstPortfolio) {
      handleSelectPortfolio(firstPortfolio.id);
    }
  }, [handleSelectPortfolio, portfolios]);

  const handleDeleteProperty = useCallback(async (id: string) => {
    const property = properties[id] || null;
    deleteProperty(id);
    if (activePropertyId === id) {
      const remainingIds = propertyOrder.filter((propertyId: string) => propertyId !== id);
      if (remainingIds.length > 0) {
        loadProperty(remainingIds[0]);
      } else {
        handleNewProperty();
      }
    }
    if (!user || !property?.supabaseId) return;

    const scopedQuery = applyOwnershipScope(
      supabase
        .from('properties')
        .delete()
        .eq('id', property.supabaseId),
      user.uid,
      activeTeamId
    );
    const { error } = await scopedQuery;

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Property deleted locally, but Supabase delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }

    setPropertySyncStatus({
      tone: 'success',
      message: 'Property deleted from Supabase.'
    });
  }, [activePropertyId, activeTeamId, deleteProperty, handleNewProperty, loadProperty, properties, propertyOrder, user]);

  const handleDeletePortfolio = useCallback(async (id: string) => {
    deletePortfolio(id);
    if (activePortfolioId === id) {
      setActivePortfolioId(null);
      setActiveWorkspace('property');
    }

    if (!user) return;

    const scopedQuery = applyOwnershipScope(
      supabase
        .from('portfolios')
        .delete()
        .eq('id', id),
      user.uid,
      activeTeamId
    );
    const { error } = await scopedQuery;

    if (error) {
      setPropertySyncStatus({
        tone: 'error',
        message: `Portfolio deleted locally, but Supabase delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
      });
      return;
    }

    setPropertySyncStatus({
      tone: 'success',
      message: 'Portfolio deleted from Supabase.'
    });
  }, [activePortfolioId, activeTeamId, deletePortfolio, user]);

  const handleAddToPortfolio = useCallback((propertyId: string, portfolioId: string) => {
    const result = addPropertyToPortfolio(portfolioId, propertyId);
    if (result !== 'added' || !user) return result;

    const remotePropertyId = resolveSupabasePropertyId(propertyId, properties);
    if (!remotePropertyId) {
      setPropertySyncStatus({
        tone: 'info',
        message: 'Portfolio membership saved locally, but Supabase insert was skipped because this property does not have a synced Supabase id yet.'
      });
      return result;
    }

    void (async () => {
      const { error } = await supabase
        .from('portfolio_properties')
        .insert({
          ...buildOwnershipPayload(user.uid, activeTeamId),
          portfolio_id: portfolioId,
          property_id: remotePropertyId
        });

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Portfolio membership saved locally, but Supabase insert failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
        return;
      }

      setPropertySyncStatus({
        tone: 'success',
        message: 'Portfolio membership saved to Supabase.'
      });
    })();

    return result;
  }, [activeTeamId, addPropertyToPortfolio, properties, user]);

  const handleRemoveFromPortfolio = useCallback((portfolioId: string, propertyId: string) => {
    removePropertyFromPortfolio(portfolioId, propertyId);

    if (!user) return;

    const remotePropertyId = resolveSupabasePropertyId(propertyId, properties);
    if (!remotePropertyId) return;

    void (async () => {
      const scopedQuery = applyOwnershipScope(
        supabase
          .from('portfolio_properties')
          .delete()
          .eq('portfolio_id', portfolioId)
          .eq('property_id', remotePropertyId),
        user.uid,
        activeTeamId
      );
      const { error } = await scopedQuery;

      if (error) {
        setPropertySyncStatus({
          tone: 'error',
          message: `Portfolio membership removed locally, but Supabase delete failed: ${error.message}${error.code ? ` (${error.code})` : ''}`
        });
        return;
      }

      setPropertySyncStatus({
        tone: 'success',
        message: 'Portfolio membership removed from Supabase.'
      });
    })();
  }, [activeTeamId, properties, removePropertyFromPortfolio, user]);

  const downloadCSV = () => {
    const headers = ['Month', 'Period', 'Beginning Loan Balance', 'Monthly NOI', 'Gross Rent', 'Vacancy Loss', 'Effective Rent', 'Other Income', 'Taxes', 'Insurance', 'HOA', 'Mgmt', 'Maintenance', 'Operating CF Before Reserves', 'Repair Reserve', 'Turnover Reserve', 'Utilities', 'Scheduled Debt Service', 'Interest Paid', 'Principal Paid', 'Total Debt Service', 'CapEx Events', 'Make Ready', 'Ending CF After Reserves', 'Reserve Balance', 'Ending Loan Balance', 'Remaining Loan Balance', 'Cumulative Principal Paid'];
    const rows = modelResult.monthlyProjection.map((row: MonthlyProjectionRow) => [
      row.month.toString(),
      row.periodStart,
      row.beginningLoanBalance.toFixed(2),
      row.monthlyNoi.toFixed(2),
      row.grossScheduledRent.toFixed(2),
      row.vacancyLoss.toFixed(2),
      row.effectiveRentalIncome.toFixed(2),
      row.otherIncome.toFixed(2),
      row.propertyTaxes.toFixed(2),
      row.insurance.toFixed(2),
      row.hoa.toFixed(2),
      row.propertyManagement.toFixed(2),
      row.routineMaintenance.toFixed(2),
      row.operatingCashFlowBeforeReserves.toFixed(2),
      row.repairReserve.toFixed(2),
      row.turnoverReserve.toFixed(2),
      row.utilities.toFixed(2),
      row.scheduledDebtService.toFixed(2),
      row.interestPaid.toFixed(2),
      row.principalPaid.toFixed(2),
      row.totalDebtService.toFixed(2),
      row.capexEvents.toFixed(2),
      row.makeReadyCosts.toFixed(2),
      row.endingCashFlow.toFixed(2),
      row.endingReserveBalance.toFixed(2),
      row.endingLoanBalance.toFixed(2),
      row.remainingLoanBalance.toFixed(2),
      row.cumulativePrincipalPaid.toFixed(2)
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + headers.join(',') + '\n' + rows.map((e: string[]) => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${propertyName.replace(/\s+/g, '_')}_wrennen_underwriting.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentYearUtilitiesAnnual = inputs.utilitiesMonthly * 12;
  const activeHistoricalActual = timelineEditorYearId ? historicalActuals.find((row: HistoricalActualYear) => row.id === timelineEditorYearId) || null : null;
  const editingCurrentYear = timelineEditorMode === 'edit-current';

  const updateHistoricalActual = useCallback((id: string, updates: Partial<HistoricalActualYear>) => {
    setHistoricalActuals((prev: HistoricalActualYear[]) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)).sort((a, b) => a.year - b.year));
  }, []);

  const deleteHistoricalActual = useCallback((id: string) => {
    setHistoricalActuals((prev: HistoricalActualYear[]) => prev.filter((row) => row.id !== id));
    setTimelineEditorMode((current: null | 'create-actual' | 'edit-actual' | 'edit-current') => (timelineEditorYearId === id && current === 'edit-actual' ? null : current));
    setTimelineEditorYearId((current: string | null) => (current === id ? null : current));
  }, [timelineEditorYearId]);

  const openCreateActualEditor = useCallback(() => {
    const existingYears = historicalActuals.map((row: HistoricalActualYear) => row.year);
    const nextYear = existingYears.length ? Math.min(...existingYears) - 1 : baseYear - 1;
    const newRow: HistoricalActualYear = {
      id: crypto.randomUUID(),
      year: Math.max(1900, nextYear),
      annualRent: 0,
      annualTaxes: 0,
      annualInsurance: 0,
      annualHoa: 0,
      annualMaintenance: 0,
      utilities: 0,
      notes: ''
    };
    setHistoricalActuals((prev: HistoricalActualYear[]) => [...prev, newRow].sort((a, b) => a.year - b.year));
    setTimelineEditorYearId(newRow.id);
    setTimelineEditorMode('create-actual');
  }, [baseYear, historicalActuals]);

  const closeTimelineEditor = useCallback(() => {
    if (timelineEditorMode === 'create-actual' && activeHistoricalActual) {
      const isEmptyRow = activeHistoricalActual.annualRent === 0 &&
        activeHistoricalActual.annualTaxes === 0 &&
        activeHistoricalActual.annualInsurance === 0 &&
        activeHistoricalActual.annualHoa === 0 &&
        activeHistoricalActual.annualMaintenance === 0 &&
        activeHistoricalActual.utilities === 0 &&
        !activeHistoricalActual.notes;
      if (isEmptyRow) {
        setHistoricalActuals((prev: HistoricalActualYear[]) => prev.filter((row) => row.id !== activeHistoricalActual.id));
      }
    }
    setTimelineEditorMode(null);
    setTimelineEditorYearId(null);
  }, [activeHistoricalActual, timelineEditorMode]);

  const timelineBadgeStyles: Record<AnnualTimelineRow['rowType'], string> = {
    actual: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
    current: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    forecast: 'border-amber-500/30 bg-amber-500/10 text-amber-300'
  };

  const annualTimelineSection = (editable = false) => (
    <div className="rounded-[2rem] border border-slate-700/50 bg-slate-800 p-6 shadow-xl shadow-black/30">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Operating Timeline</p>
          <h3 className="mt-3 text-2xl font-black text-white">Actual, Current, And Forecast Years</h3>
          <p className="mt-2 max-w-3xl text-sm text-slate-400">Historical actuals stay manual, the current operating snapshot anchors {baseYear}, and every future row forecasts forward from that current year.</p>
        </div>
        <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
          Forecast start year: <span className="font-black text-white">{baseYear}</span>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[1200px] text-left text-[11px]">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-900 text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
              <th className="sticky left-0 z-10 bg-slate-900 px-6 py-5">Year</th>
              <th className="px-6 py-5">Type</th>
              <th className="px-6 py-5 text-right">Rent</th>
              <th className="px-6 py-5 text-right">Expenses</th>
              <th className="px-6 py-5 text-right">NOI</th>
              <th className="px-6 py-5 text-right">Debt</th>
              <th className="px-6 py-5 text-right">Net CF</th>
              <th className="px-6 py-5 text-right">Sale Proceeds</th>
              <th className="px-6 py-5 text-right">MOIC</th>
              <th className="px-6 py-5 text-right">IRR</th>
              <th className="px-6 py-5">Notes</th>
              {editable && <th className="px-6 py-5 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {modelResult.annualTimeline.map((row: AnnualTimelineRow) => (
              <tr key={row.id} className="group border-b border-slate-700/50 transition-colors hover:bg-slate-700/30">
                <td className="sticky left-0 z-10 bg-slate-800 px-6 py-4 font-black text-slate-200 group-hover:bg-slate-700/30">{row.calendarYear}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${timelineBadgeStyles[row.rowType]}`}>
                    {row.displayLabel}
                  </span>
                </td>
                <td className="px-6 py-4 text-right font-mono text-slate-300">{formatCurrency2(row.grossRent)}</td>
                <td className="px-6 py-4 text-right font-mono text-rose-300">-{formatCurrency2(row.expenses)}</td>
                <td className="px-6 py-4 text-right font-black text-slate-200">{formatCurrency2(row.noi)}</td>
                <td className="px-6 py-4 text-right font-mono text-rose-400">{row.debtService === null ? '—' : `-${formatCurrency2(row.debtService)}`}</td>
                <td className="px-6 py-4 text-right font-black text-emerald-300">{row.operatingCashFlow === null ? '—' : formatCurrency2(row.operatingCashFlow)}</td>
                <td className="px-6 py-4 text-right font-mono text-slate-300">{row.netSaleProceeds === null ? '—' : formatCurrency0(row.netSaleProceeds)}</td>
                <td className="px-6 py-4 text-right font-bold text-cyan-300">{row.moic === null ? '—' : `${formatNumber2(row.moic)}x`}</td>
                <td className="px-6 py-4 text-right font-bold text-amber-300">{formatPercent2(row.irr)}</td>
                <td className="px-6 py-4 text-slate-500">{row.notes || '—'}</td>
                {editable && (
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      {row.rowType === 'actual' && (
                        <>
                          <button
                            onClick={() => {
                              setTimelineEditorYearId(row.id);
                              setTimelineEditorMode('edit-actual');
                            }}
                            className="rounded-xl border border-slate-600 bg-slate-900/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteHistoricalActual(row.id)}
                            className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-rose-300"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {row.rowType === 'current' && (
                        <>
                          <button
                            onClick={() => {
                              setTimelineEditorYearId('current-base-year');
                              setTimelineEditorMode('edit-current');
                            }}
                            className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300"
                          >
                            Edit
                          </button>
                          <span className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                            Base Year Locked
                          </span>
                        </>
                      )}
                      {row.rowType === 'forecast' && (
                        <span className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                          Model Generated
                        </span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (authLoading && !bootTimeoutElapsed) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium animate-pulse">Initializing Wrennen Platform...</p>
        </div>
      </div>
    );
  }

  if (showLanding) {
    return (
      <>
        <LandingPage
          onStart={handleStartAnalysis}
          onLogin={() => setAuthMode('login')}
          onSignUp={() => setAuthMode('signup')}
        />
        {authMode ? <AuthScreen initialMode={authMode} onSuccess={() => setShowLanding(false)} onClose={() => setAuthMode(null)} /> : null}
      </>
    );
  }

  if (!user) {
    return (
      <>
        <LandingPage
          onStart={handleStartAnalysis}
          onLogin={() => setAuthMode('login')}
          onSignUp={() => setAuthMode('signup')}
        />
        {authMode ? <AuthScreen initialMode={authMode} onSuccess={() => setShowLanding(false)} onClose={() => setAuthMode(null)} /> : null}
      </>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-slate-950 text-slate-100 overflow-hidden">
      <Sidebar
        themeMode={themeMode}
        onToggleTheme={() => setThemeMode((currentTheme: ThemeMode) => currentTheme === 'light' ? 'dark' : 'light')}
        properties={properties}
        portfolios={portfolios}
        debts={activeWorkspace === 'debt' ? debts : (activePropertyId ? propertyScopedDebts : debts)}
        propertyOrder={propertyOrder}
        activePropertyId={activePropertyId || ''}
        activeDebtScopePropertyId={activeWorkspace === 'debt' ? null : activeDebtScopePropertyId}
        activePortfolioId={activePortfolioId}
        activeDebtId={selectedDebtId}
        isWorkspaceReloading={isWorkspaceReloading}
        isDebtWorkspaceActive={activeWorkspace === 'debt'}
        isTaxWorkspaceActive={activeWorkspace === 'tax'}
        onSelectProperty={loadProperty}
        onSelectPortfolio={handleSelectPortfolio}
        onSelectDebtWorkspace={handleSelectDebtWorkspace}
        onSelectTaxWorkspace={handleSelectTaxWorkspace}
        onSelectDebt={handleSelectDebtFromSidebar}
        onDeleteProperty={handleDeleteProperty}
        onDeletePortfolio={handleDeletePortfolio}
        onNewProperty={handleNewProperty}
        onCreatePortfolio={handleCreatePortfolio}
        onViewPortfolio={handleViewPortfolio}
        onReorderProperties={handleReorderProperties}
        onAddToPortfolio={(propertyId: string, portfolioId: string) => handleAddToPortfolio(propertyId, portfolioId)}
        onRemoveFromPortfolio={handleRemoveFromPortfolio}
        onGoHome={() => {
          handleOpenPropertyDashboard();
        }}
        user={user}
        teams={teams}
        activeTeamId={activeTeamId}
        teamMembers={teamMembers}
        teamStatus={teamStatus}
        syncStatus={workspaceSyncStatus}
        onSelectTeam={setActiveTeamId}
        onAddTeamMember={handleAddTeamMember}
        onRemoveTeamMember={handleRemoveTeamMember}
        onLogout={handleLogout}
      />

      <main className="flex-grow h-screen overflow-y-auto p-6 lg:p-10 bg-slate-950 relative">
        <div className="max-w-7xl mx-auto">
          {propertySyncStatus ? (
            <div className={`mb-6 rounded-2xl border px-4 py-3 text-sm ${
              propertySyncStatus.tone === 'success'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                : propertySyncStatus.tone === 'error'
                  ? 'border-rose-500/30 bg-rose-500/10 text-rose-200'
                  : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100'
            }`}>
              {propertySyncStatus.message}
            </div>
          ) : null}
          {activePortfolioId ? (
            isActivePortfolioHydrating || debtLayerLoadState === 'loading' ? (
              <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-10">
                <div className="flex min-h-[32vh] flex-col items-center justify-center text-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500/20 border-t-emerald-400" />
                  <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Loading Portfolio Details</p>
                  <p className="mt-2 max-w-xl text-sm text-slate-400">We’re fetching full property records and linked debt only for this portfolio.</p>
                </div>
              </div>
            ) : (
            <PortfolioView
              portfolio={portfolios[activePortfolioId]}
              properties={properties}
              debts={debts}
              debtLinks={debtLinks}
              debtEvents={debtEvents}
              onRemoveProperty={(propId: string) => handleRemoveFromPortfolio(activePortfolioId, propId)}
            />
            )
          ) : activeWorkspace === 'debt' ? (
            canPersistDebt ? (
              debtLayerLoadState === 'loading' ? (
                <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-10">
                  <div className="flex min-h-[32vh] flex-col items-center justify-center text-center">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500/20 border-t-emerald-400" />
                    <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Loading Debt Workspace</p>
                    <p className="mt-2 max-w-xl text-sm text-slate-400">Debt records, links, and events are being fetched in parallel now that you’ve opened Debt Desk.</p>
                  </div>
                </div>
              ) : (
              <DebtView
                debts={debts}
                debtLinks={debtLinks}
                debtEvents={debtEvents}
                properties={properties}
                activePropertyId={activeSupabasePropertyId}
                requestedDebtId={selectedDebtId}
                onSelectedDebtChange={setSelectedDebtId}
                onSaveDebt={handleSaveDebt}
                onDeleteDebt={handleDeleteDebt}
                onSaveDebtEvent={handleSaveDebtEvent}
                onDeleteDebtEvent={handleDeleteDebtEvent}
                onUpsertDebtLink={handleUpsertDebtLink}
                onRemoveDebtLink={handleRemoveDebtLink}
                onAutoAllocateDebtByAcquisitionPrice={autoAllocateDebtByAcquisitionPrice}
              />
              )
            ) : (
              <div className="rounded-[2rem] border border-amber-500/30 bg-amber-500/10 p-8 text-amber-100 shadow-xl shadow-black/20">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">Debt Sync Blocked</p>
                <h2 className="mt-3 text-2xl font-black text-white">Save this property before creating debt</h2>
                <p className="mt-3 max-w-2xl text-sm text-amber-100/90">
                  Debt rows in Supabase require a valid property id from the `properties` table. Save the active property first, then come back to Debt Desk and the debt record will persist normally.
                </p>
              </div>
            )
          ) : activeWorkspace === 'tax' ? (
            isTaxWorkspaceHydrating || debtLayerLoadState === 'loading' ? (
              <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-10">
                <div className="flex min-h-[32vh] flex-col items-center justify-center text-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500/20 border-t-emerald-400" />
                  <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Preparing Tax Readiness</p>
                  <p className="mt-2 max-w-xl text-sm text-slate-400">We’re hydrating property details and debt schedules only because you opened the tax workspace.</p>
                </div>
              </div>
            ) : (
              <TaxReadinessView
                properties={properties}
                debts={debts}
                debtLinks={debtLinks}
                debtEvents={debtEvents}
                selectedYear={selectedTaxYear}
                onSelectYear={setSelectedTaxYear}
              />
            )
          ) : activeWorkspace === 'property' && propertyViewMode !== 'dashboard' ? (
            <BasePropertyForm
              propertyName={propertyName}
              address={propertyAddress}
              ownershipEntity={propertyOwnershipEntity}
              ownershipEntityOptions={ownershipEntityOptions}
              baseYear={baseYear}
              inputs={inputs}
              isSaved={isCurrentSaved}
              step={
                propertyViewMode === 'create-core' || propertyViewMode === 'edit-core'
                  ? 'core'
                  : propertyViewMode === 'create-operations' || propertyViewMode === 'edit-operations'
                    ? 'operations'
                    : 'forecast'
              }
              mode={propertyViewMode === 'edit-core' || propertyViewMode === 'edit-operations' || propertyViewMode === 'edit-forecast' ? 'edit' : 'create'}
              onNameChange={setPropertyName}
              onAddressChange={setPropertyAddress}
              onOwnershipEntityChange={setPropertyOwnershipEntity}
              onBaseYearChange={setBaseYear}
              onInputChange={updateInput}
              onContinue={handleContinuePropertyIntake}
              onBack={handleBackPropertyIntake}
              onSave={handleSaveProperty}
            />
          ) : activeWorkspace === 'property' && !activePropertyId ? (
            <PropertyDashboardHome
              properties={propertyDashboardProperties}
              onOpenProperty={loadProperty}
              onNewProperty={handleNewProperty}
              emptyStateTitle={propertyDashboardEmptyState.title}
              emptyStateDescription={propertyDashboardEmptyState.description}
            />
          ) : activeWorkspace === 'property' && activePropertyId && (!activePropertyIsHydrated || isActivePropertyHydrating) ? (
            <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-10">
              <div className="flex min-h-[32vh] flex-col items-center justify-center text-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500/20 border-t-emerald-400" />
                <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Loading Property Details</p>
                <p className="mt-2 max-w-xl text-sm text-slate-400">
                  Pulling the full property record and deferring model calculations until the detailed data is ready.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-10">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-3xl font-black tracking-tight text-white">{propertyName}</h2>
                    {propertyAddress ? <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{propertyAddress}</span> : null}
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    Underwrite quickly, save the property, then keep the Risk Dashboard updated as the real-world condition changes.
                  </p>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    {lastSavedAt ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}` : 'Not saved yet'}
                  </div>
                  <button
                    onClick={handleEditProperty}
                    disabled={isSavingProperty}
                    className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSavingProperty ? 'Saving...' : (isCurrentSaved ? 'Update Property' : 'Complete Property Setup')}
                  </button>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                <div className="flex items-center gap-1 bg-slate-800/50 p-1.5 rounded-2xl w-fit border border-slate-700/50">
                  <button onClick={() => setActiveTab('dashboard')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'dashboard' ? 'bg-slate-900 text-emerald-500 shadow-md border border-slate-600/50' : 'text-slate-400 hover:text-slate-300'}`}>
                    <BarChart3 size={14} /> Asset Intel
                  </button>
                  <button onClick={() => setActiveTab('analysis')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'analysis' ? 'bg-slate-900 text-emerald-500 shadow-md border border-slate-600/50' : 'text-slate-400 hover:text-slate-300'}`}>
                    <TableIcon size={14} /> Data Grid
                  </button>
                  <button onClick={() => setActiveTab('risk')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'risk' ? 'bg-slate-900 text-emerald-500 shadow-md border border-slate-600/50' : 'text-slate-400 hover:text-slate-300'}`}>
                    <ShieldCheck size={14} /> Risk Dashboard
                  </button>
                </div>

                {activeTab === 'analysis' && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={downloadCSV}
                      className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-300 hover:bg-slate-700 transition-all shadow-sm"
                    >
                      <FileSpreadsheet size={14} className="text-emerald-500" /> Export Data
                    </button>
                    <button
                      onClick={() => window.print()}
                      className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-300 hover:bg-slate-700 transition-all shadow-sm"
                    >
                      <Printer size={14} className="text-slate-500" /> Export PDF
                    </button>
                  </div>
                )}
              </div>

              {activeTab === 'dashboard' && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Dashboard Year View</p>
                      <p className="mt-2 text-sm text-slate-500">Select the current year snapshot or a future forecast year to see whether the investment clears the hurdle on returns, cash flow, and exit liquidity.</p>
                    </div>
                    <div className="flex w-full flex-col gap-4 md:w-auto md:flex-row md:items-end">
                      <div className="w-full md:w-52">
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Viewing Year</label>
                        <select
                          value={activeAnalysisYear}
                          onChange={(e: any) => setSelectedAnalysisYear(Number((e.target as HTMLSelectElement).value))}
                          className="w-full rounded-2xl border border-slate-700/70 bg-slate-900/80 px-4 py-3 text-sm font-black text-slate-100 outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                        >
                          {Array.from({ length: availableAnalysisYears }, (_, index) => index + 1).map((year) => (
                            <option key={year} value={year}>
                              {baseYear + year - 1} {year === 1 ? '(Current)' : '(Forecast)'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-full md:w-auto">
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Dashboard View</label>
                        <div className="grid w-full grid-cols-2 rounded-2xl border border-slate-700/70 bg-slate-900/80 p-1 md:min-w-[248px]">
                          {(['simple', 'advanced'] as DashboardDetailView[]).map((view) => (
                            <button
                              key={view}
                              onClick={() => setDashboardDetailView(view)}
                              className={`min-w-0 rounded-xl px-3 py-3 text-center text-[10px] font-black uppercase tracking-[0.14em] transition-all ${dashboardDetailView === view ? 'bg-emerald-500/15 text-emerald-300' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                              {view === 'simple' ? 'Simple View' : 'Advanced View'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="w-full md:w-auto">
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Debt View</label>
                        <div className="grid w-full grid-cols-2 rounded-2xl border border-slate-700/70 bg-slate-900/80 p-1 md:min-w-[320px]">
                          {([
                            { value: 'actual_only', label: 'Actual Only' },
                            { value: 'include_allocated_shared', label: 'Include Shared' }
                          ] as const).map((mode) => (
                            <button
                              key={mode.value}
                              onClick={() => setPropertyDebtViewMode(mode.value)}
                              className={`min-w-0 rounded-xl px-3 py-3 text-center text-[10px] font-black uppercase tracking-[0.14em] transition-all ${propertyDebtViewMode === mode.value ? 'bg-emerald-500/15 text-emerald-300' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                              {mode.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-10 rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-4 sm:p-5">
                    <div className="mb-5 flex items-center justify-between gap-4 px-2">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Investment Snapshot</p>
                        <p className="mt-2 text-sm text-slate-500">A faster read on income, stability, and capital efficiency before you drop into deeper underwriting detail.</p>
                        <p className="mt-2 text-xs text-slate-500">
                          {propertyDebtViewMode === 'include_allocated_shared'
                            ? 'Shared portfolio debt is included here as an analytical allocation only.'
                            : 'Only debt directly tied to this property is included in the analysis.'}
                        </p>
                      </div>
                      {riskSummary && (
                        <span className={`inline-flex rounded-full border px-3 py-1.5 text-[9px] font-black uppercase tracking-widest ${propertyRiskTone.badge}`}>
                          {propertyRiskTone.label}
                        </span>
                      )}
                    </div>
                    {financialSnapshotScores && (
                      <FinancialSnapshot
                        scores={{
                          performance: {
                            label: 'Performance Score',
                            value: financialSnapshotScores.performanceScore,
                            description: financialSnapshotDescriptions.performance
                          },
                          stability: {
                            label: 'Stability Score',
                            value: financialSnapshotScores.stabilityScore,
                            description: financialSnapshotDescriptions.stability
                          },
                          efficiency: {
                            label: 'Efficiency Score',
                            value: financialSnapshotScores.efficiencyScore,
                            description: financialSnapshotDescriptions.efficiency
                          }
                        }}
                        metrics={[
                          {
                            label: 'Monthly Cash Flow',
                            value: formatCurrency0(activeYearMonthlyCashFlow),
                            subtext: `Average monthly result in ${activeCalendarYear}`,
                            icon: 'cashflow'
                          },
                          {
                            label: 'Cash on Cash Return',
                            value: formatPercent2(activeYearCoC),
                            subtext: `Cash yield on invested capital in ${activeCalendarYear}`,
                            icon: 'return'
                          },
                          {
                            label: 'Cap Rate',
                            value: formatPercent2(activeYearCapRate),
                            subtext: `NOI relative to purchase price in ${activeCalendarYear}`,
                            icon: 'caprate'
                          },
                          {
                            label: 'Debt Pressure',
                            value: riskSummary ? formatPercent2(riskSummary.debtPressure * 100) : 'N/A',
                            subtext: 'Debt service load against operating income',
                            icon: 'debt'
                          },
                          {
                            label: 'Total Cash Invested',
                            value: formatCurrency0(modelResult.metrics.totalInitialInvestment),
                            subtext: 'All investor cash in at acquisition',
                            icon: 'invested'
                          }
                        ]}
                      />
                    )}
                  </div>

                  <div className="mb-10">
                    <div className="mb-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Key Risks / Alerts</p>
                      <p className="mt-2 text-sm text-slate-500">These are the few issues most likely to change the investment call or the next operating move.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                      {(riskSummary?.actions.slice(0, 3) || []).map((action: any) => (
                        <div key={action.id} className="rounded-[1.75rem] border border-slate-700/60 bg-slate-900 p-5">
                          <p className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
                            action.priority === 'high'
                              ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                              : action.priority === 'medium'
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                                : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                          }`}>
                            {action.priority} priority
                          </p>
                          <p className="mt-4 text-lg font-black text-white">{action.title}</p>
                          <p className="mt-3 text-sm leading-relaxed text-slate-400">{action.detail}</p>
                        </div>
                      ))}
                      {(!riskSummary || riskSummary.actions.length === 0) && (
                        <div className="rounded-[1.75rem] border border-slate-700/60 bg-slate-900 p-5 xl:col-span-3">
                          <p className="text-lg font-black text-white">No urgent alerts are currently flagged.</p>
                          <p className="mt-2 text-sm text-slate-400">The model is still running in full, but the dashboard no longer fills the page with lower-value metrics by default.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {dashboardDetailView === 'advanced' && (
                    <>
                    <div className="mb-10 rounded-[2rem] border border-slate-700/60 bg-slate-900/70 p-4 sm:p-5">
                      <div className="mb-4 flex items-center justify-between gap-4 px-2">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Detailed Return Metrics</p>
                          <p className="mt-2 text-sm text-slate-500">Advanced view brings back the fuller return stack once you want to inspect the underwriting in detail.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                        <SummaryCard label="Levered IRR" value={formatPercent2(activeYearData?.irr ?? null)} subtext={activeYearData?.irr === null ? `No valid exit IRR in ${activeCalendarYear}` : `If sold at end of ${activeCalendarYear}`} icon={<TrendingUp size={20} />} emphasis="featured" />
                        <SummaryCard label="MOIC" value={`${formatNumber2(activeYearData?.moic || 0)}x`} subtext={`MOIC if sold at end of ${activeCalendarYear}`} icon={<ChevronRight size={20} />} emphasis="featured" />
                        <SummaryCard label="Monthly True Return" value={formatCurrency0(activeYearMonthlyTrueReturn)} subtext={`Cash flow + equity growth in ${activeCalendarYear}`} icon={<TrendingUp size={20} />} emphasis="featured" />
                      </div>
                    </div>
                    <div className="mb-10 grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr_0.95fr]">
                      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                        <div className="mb-5">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Financing / Debt</p>
                          <p className="mt-2 text-sm text-slate-500">Financing mechanics stay available here without competing with the primary investment call.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Linked Debt</p>
                            <p className="mt-2 text-lg font-black text-white">{modelResult.debtSnapshot?.debtName || 'Unlevered'}</p>
                            <p className="mt-2 text-xs text-slate-500">{modelResult.debtSnapshot ? `${modelResult.debtSnapshot.lender || 'Debt record'} linked to this property` : 'No linked debt on this property'}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocation Method</p>
                            <p className="mt-2 text-lg font-black text-white">{modelResult.metrics.allocationMethod ? modelResult.metrics.allocationMethod.replace(/_/g, ' ') : 'None'}</p>
                            <p className="mt-2 text-xs text-slate-500">How shared debt is assigned to this property.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocation %</p>
                            <p className="mt-2 text-lg font-black text-emerald-300">{formatPercent2(modelResult.metrics.allocationPercent)}</p>
                            <p className="mt-2 text-xs text-slate-500">Share of linked debt carried by this asset.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocated Debt Service</p>
                            <p className="mt-2 text-lg font-black text-white">{formatCurrency0(modelResult.metrics.allocatedMonthlyDebtService)}</p>
                            <p className="mt-2 text-xs text-slate-500">Monthly payment allocated into this property model.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocated Loan Balance</p>
                            <p className="mt-2 text-lg font-black text-white">{formatCurrency0(modelResult.metrics.allocatedCurrentLoanBalance)}</p>
                            <p className="mt-2 text-xs text-slate-500">Current debt balance assigned to the property.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Debt Terms</p>
                            <p className="mt-2 text-lg font-black text-white">{formatPercent2(modelResult.metrics.allocatedInterestRate || modelResult.debtSnapshot?.interestRate || null)}</p>
                            <p className="mt-2 text-xs text-slate-500">{modelResult.debtSnapshot ? `${modelResult.debtSnapshot.amortYears}-year amortization${modelResult.debtSnapshot.interestOnly ? `, ${modelResult.debtSnapshot.interestOnlyMonths} months IO` : ''}` : 'No debt terms applied'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                        <div className="mb-5">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Advanced / Details</p>
                          <p className="mt-2 text-sm text-slate-500">Amortization and cost-of-debt details stay accessible here for deeper underwriting review.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Principal Paid</p>
                            <p className="mt-2 text-lg font-black text-cyan-300">{formatCurrency0(activeYearData?.principalPaid || 0)}</p>
                            <p className="mt-2 text-xs text-slate-500">Principal paydown during Year {activeAnalysisYear}.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Interest Paid</p>
                            <p className="mt-2 text-lg font-black text-rose-300">{formatCurrency0(activeYearData?.interestPaid || 0)}</p>
                            <p className="mt-2 text-xs text-slate-500">Debt cost during Year {activeAnalysisYear}.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Avg Monthly Principal</p>
                            <p className="mt-2 text-lg font-black text-cyan-300">{formatCurrency0((activeYearData?.principalPaid || 0) / activeYearMonthCount)}</p>
                            <p className="mt-2 text-xs text-slate-500">Average monthly equity gained from amortization.</p>
                          </div>
                          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Avg Monthly Interest</p>
                            <p className="mt-2 text-lg font-black text-rose-300">{formatCurrency0((activeYearData?.interestPaid || 0) / activeYearMonthCount)}</p>
                            <p className="mt-2 text-xs text-slate-500">Average monthly debt expense in Year {activeAnalysisYear}.</p>
                          </div>
                        </div>
                        <div className="mt-4 rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div>
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Annual Debt Service</p>
                              <p className="mt-2 text-xl font-black text-white">{formatCurrency0(activeYearData?.debtService || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ending Loan Balance</p>
                              <p className="mt-2 text-xl font-black text-white">{formatCurrency0(activeYearMonths[activeYearMonths.length - 1]?.endingLoanBalance || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cumulative Principal Paid</p>
                              <p className="mt-2 text-xl font-black text-cyan-300">{formatCurrency0(activeYearMonths[activeYearMonths.length - 1]?.cumulativePrincipalPaid || 0)}</p>
                            </div>
                          </div>
                          <p className="mt-3 text-xs text-slate-500">This amortization breakdown is built from the monthly schedule and stays in the advanced view only.</p>
                        </div>
                      </div>
                    </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-slate-800 p-8 rounded-[3rem] border border-slate-700/50 shadow-xl shadow-black/30">
                      <div className="flex items-start justify-between gap-4 mb-6">
                        <div>
                          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                            <TrendingUp size={12} className="text-emerald-500" /> Cash Flow Profile
                          </h3>
                          <p className="text-[10px] text-slate-500 mt-3">Native monthly cash flow rows with real month-by-month debt service, reserves, vacancy drag, and event timing.</p>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900/70 border border-slate-700/70 rounded-2xl p-1">
                          {CHART_RANGES.map((range) => (
                            <button
                              key={range}
                              onClick={() => setChartRangeMonths(range)}
                              className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${chartRangeMonths === range ? 'bg-emerald-500/15 text-emerald-300' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                              {range}m
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData}>
                            <defs>
                              <linearGradient id="colorCf" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" />
                            <XAxis
                              dataKey="month"
                              axisLine={false}
                              tickLine={false}
                              ticks={chartTicks}
                              tick={{fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', fill: '#94a3b8'}}
                              dy={10}
                              tickFormatter={(value: number) => `${value}`}
                              label={{ value: 'Months', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 10, fontWeight: 900 }}
                            />
                            <YAxis axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', fill: '#94a3b8'}} tickFormatter={(v: number) => `$${v/1000}k`} />
                            <Tooltip
                              contentStyle={{borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5)', backgroundColor: '#1e293b', color: '#f8fafc'}}
                              formatter={(v: number) => formatCurrency2(v)}
                              labelFormatter={(label: number) => `Month ${label}`}
                            />
                            <Area type="monotone" dataKey="endingCashFlow" name="Monthly Cash Flow" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorCf)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-slate-800 p-8 rounded-[3rem] border border-slate-700/50 shadow-xl shadow-black/30">
                      <div className="mb-6">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <Percent size={12} className="text-emerald-500" /> Return Benchmarks
                        </h3>
                        <p className="text-[10px] text-slate-500 mt-3">Cash flow stays strictly spendable cash after full debt service. True return adds non-cash equity growth from principal paydown and appreciation.</p>
                      </div>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" />
                            <XAxis
                              dataKey="month"
                              axisLine={false}
                              tickLine={false}
                              ticks={chartTicks}
                              tick={{fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', fill: '#94a3b8'}}
                              dy={10}
                              tickFormatter={(value: number) => `${value}`}
                              label={{ value: 'Months', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 10, fontWeight: 900 }}
                            />
                            <YAxis axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', fill: '#94a3b8'}} tickFormatter={(v: number) => `$${Math.round(v/1000)}k`} />
                            <Tooltip
                              contentStyle={{borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5)', backgroundColor: '#1e293b', color: '#f8fafc'}}
                              formatter={(v: number, name: string) => [formatCurrency2(v), name]}
                              labelFormatter={(label: number) => `Month ${label}`}
                            />
                            <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{fontSize: '9px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', paddingBottom: '20px', color: '#cbd5e1'}} />
                            <Line type="monotone" dataKey="endingCashFlow" name="Monthly Cash Flow" stroke="#10b981" strokeWidth={3} dot={{r: 0}} activeDot={{r: 6}} />
                            <Line type="monotone" dataKey="monthlyTrueReturn" name="Monthly True Return" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Annual Expense Stack</p>
                      <p className="text-3xl font-black text-white mt-3">{formatCurrency0(activeYearData?.expenses || 0)}</p>
                      <p className="text-xs text-slate-500 mt-3">Operating expenses in Year {activeAnalysisYear}, rolled up from that year&apos;s monthly rows.</p>
                    </div>
                    <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Effective Gross Income</p>
                      <p className="text-3xl font-black text-white mt-3">{formatCurrency0(activeYearEgi)}</p>
                      <p className="text-xs text-slate-500 mt-3">Rolled up from the monthly schedule for Year {activeAnalysisYear}.</p>
                    </div>
                    <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Projected Total Profit</p>
                      <p className="text-3xl font-black text-emerald-300 mt-3">{formatCurrency0(activeYearExitProfit)}</p>
                      <p className="text-xs text-slate-500 mt-3">Profit if sold at the end of Year {activeAnalysisYear}, including reserve release.</p>
                    </div>
                  </div>

                  <div className={`mt-8 grid grid-cols-1 gap-6 ${reserveTransferCards.length === 1 ? 'xl:grid-cols-2' : 'xl:grid-cols-4'}`}>
                    <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Op CF Before Reserves</p>
                      <p className="text-3xl font-black text-white mt-3">{formatCurrency0(activeYearOperatingCashFlowBeforeReserves)}</p>
                      <p className="text-xs text-slate-500 mt-3">Operating cash flow after debt service, before reserve transfers in Year {activeAnalysisYear}.</p>
                    </div>
                    {reserveTransferCards.map((card) => (
                      <div key={card.label} className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{card.label}</p>
                        <p className={`text-3xl font-black mt-3 ${card.tone}`}>{formatCurrency0(card.value)}</p>
                        <p className="text-xs text-slate-500 mt-3">{card.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Cash Flow Vs True Return</p>
                    <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 mt-5">
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 overflow-hidden">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="border-b border-slate-700 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                              <th className="px-5 py-4">Return Driver</th>
                              <th className="px-5 py-4 text-right">Monthly</th>
                              <th className="px-5 py-4 text-right">Annual</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-slate-800">
                              <td className="px-5 py-4 text-slate-200">Op CF Before Reserves</td>
                              <td className="px-5 py-4 text-right font-mono text-slate-200">{formatCurrency2(activeYearOperatingCashFlowBeforeReserves / activeYearMonthCount)}</td>
                              <td className="px-5 py-4 text-right font-mono text-slate-200">{formatCurrency0(activeYearOperatingCashFlowBeforeReserves)}</td>
                            </tr>
                            <tr className="border-b border-slate-800">
                              <td className="px-5 py-4 text-slate-200">Reserve Funding</td>
                              <td className="px-5 py-4 text-right font-mono text-cyan-300">-{formatCurrency2(activeYearReserveFunding / activeYearMonthCount)}</td>
                              <td className="px-5 py-4 text-right font-mono text-cyan-300">-{formatCurrency0(activeYearReserveFunding)}</td>
                            </tr>
                            <tr className="border-b border-slate-800">
                              <td className="px-5 py-4 text-slate-200">Cash Flow</td>
                              <td className="px-5 py-4 text-right font-mono text-emerald-300">{formatCurrency2(activeYearMonthlyCashFlow)}</td>
                              <td className="px-5 py-4 text-right font-mono text-emerald-300">{formatCurrency0(activeYearData?.operatingCf || 0)}</td>
                            </tr>
                            <tr className="border-b border-slate-800">
                              <td className="px-5 py-4 text-slate-200">Principal Paydown</td>
                              <td className="px-5 py-4 text-right font-mono text-cyan-300">{formatCurrency2((activeYearData?.principalPaid || 0) / activeYearMonthCount)}</td>
                              <td className="px-5 py-4 text-right font-mono text-cyan-300">{formatCurrency0(activeYearData?.principalPaid || 0)}</td>
                            </tr>
                            <tr className="border-b border-slate-800">
                              <td className="px-5 py-4 text-slate-200">Appreciation</td>
                              <td className="px-5 py-4 text-right font-mono text-amber-300">{formatCurrency2((activeYearData?.appreciation || 0) / activeYearMonthCount)}</td>
                              <td className="px-5 py-4 text-right font-mono text-amber-300">{formatCurrency0(activeYearData?.appreciation || 0)}</td>
                            </tr>
                            <tr>
                              <td className="px-5 py-4 font-black text-white">Total Return</td>
                              <td className="px-5 py-4 text-right font-black text-white">{formatCurrency2(activeYearMonthlyTrueReturn)}</td>
                              <td className="px-5 py-4 text-right font-black text-white">{formatCurrency0(activeYearData?.trueReturn || 0)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Monthly NOI</p>
                          <p className="text-xl font-black text-white mt-2">{formatCurrency0(activeYearNoi / activeYearMonthCount)}</p>
                          <p className="mt-2 text-xs text-slate-500">Average monthly NOI in Year {activeAnalysisYear}.</p>
                        </div>
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Annual NOI</p>
                          <p className="text-xl font-black text-white mt-2">{formatCurrency0(activeYearNoi)}</p>
                          <p className="mt-2 text-xs text-slate-500">Rolled up from the monthly engine for Year {activeAnalysisYear}.</p>
                        </div>
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cumulative Equity Built</p>
                          <p className="text-xl font-black text-cyan-300 mt-2">{formatCurrency0(modelResult.metrics.equityBuiltFromPrincipal)}</p>
                          <p className="mt-2 text-xs text-slate-500">Principal paydown through the hold period.</p>
                        </div>
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Why It Matters</p>
                          <p className="mt-2 text-sm text-slate-300">Cash flow is spendable cash. True return adds equity growth from amortization and appreciation without mislabeling either as cash in pocket.</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5">
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cash Flow During Hold</p>
                        <p className="text-xl font-black text-white mt-2">{formatCurrency0(modelResult.proForma.filter((row: YearData) => row.year <= activeAnalysisYear).reduce((sum: number, row: YearData) => sum + row.operatingCf, 0))}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Principal Paydown</p>
                        <p className="text-xl font-black text-cyan-300 mt-2">{formatCurrency0(modelResult.metrics.equityBuiltFromPrincipal)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sale Equity</p>
                        <p className="text-xl font-black text-emerald-300 mt-2">{formatCurrency0(modelResult.metrics.netSalesProceeds)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reserve Release</p>
                        <p className="text-xl font-black text-white mt-2">{formatCurrency0(activeYearData?.reserveRelease || 0)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-4">IRR is built from this annual cash flow series: initial investment outflow in Year 0, annual cash flow during the hold, and final-year sale proceeds plus reserve release. If that series never changes sign, the dashboard shows N/A instead of 0.00%.</p>
                  </div>
                </>
                  )}
                </div>
              )}

              {activeTab === 'analysis' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Operating Timeline</p>
                        <p className="mt-2 text-sm text-slate-500">This is the single place to manage actual years, the current base year, and the forward forecast timeline.</p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={openCreateActualEditor}
                          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300"
                        >
                          Add Historical Year
                        </button>
                        <button
                          onClick={() => {
                            setTimelineEditorYearId('current-base-year');
                            setTimelineEditorMode('edit-current');
                          }}
                          className="rounded-2xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-300"
                        >
                          Edit Base Year
                        </button>
                        <button
                          onClick={handleSaveProperty}
                          disabled={isSavingProperty}
                          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSavingProperty ? 'Saving...' : 'Save Timeline Changes'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {timelineEditorMode && (
                    <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
                      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                            {editingCurrentYear ? 'Current / Base Year Editor' : 'Actual Year Editor'}
                          </p>
                          <h3 className="mt-3 text-2xl font-black text-white">
                            {editingCurrentYear ? `Edit ${baseYear} Base Year` : `${timelineEditorMode === 'create-actual' ? 'Add' : 'Edit'} Historical Actual`}
                          </h3>
                          <p className="mt-2 max-w-3xl text-sm text-slate-500">
                            {editingCurrentYear
                              ? 'This row is the forecast anchor. You can update it, but it cannot be deleted because future years depend on it.'
                              : 'Historical actual rows are manually entered years that slot into the annual timeline in chronological order.'}
                          </p>
                        </div>
                        <button
                          onClick={closeTimelineEditor}
                          className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-300"
                        >
                          Close Editor
                        </button>
                      </div>

                      {editingCurrentYear ? (
                        <>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Base Year</label>
                              <input
                                type="number"
                                value={baseYear}
                                onChange={(e: any) => setBaseYear(Number(e.target.value) || INITIAL_BASE_YEAR)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Annual Rent</label>
                              <input
                                type="number"
                                value={inputs.annualRent}
                                onChange={(e: any) => updateInput('annualRent', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Annual Taxes</label>
                              <input
                                type="number"
                                value={inputs.annualTaxes}
                                onChange={(e: any) => updateInput('annualTaxes', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Annual Insurance</label>
                              <input
                                type="number"
                                value={inputs.annualInsurance}
                                onChange={(e: any) => updateInput('annualInsurance', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Annual HOA</label>
                              <input
                                type="number"
                                value={inputs.annualHoa}
                                onChange={(e: any) => updateInput('annualHoa', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Annual Maintenance</label>
                              <input
                                type="number"
                                value={inputs.annualMaintenance}
                                onChange={(e: any) => updateInput('annualMaintenance', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Utilities Monthly</label>
                              <input
                                type="number"
                                value={inputs.utilitiesMonthly}
                                onChange={(e: any) => updateInput('utilitiesMonthly', Number(e.target.value) || 0)}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Revenue</p>
                              <p className="mt-2 text-xl font-black text-white">{formatCurrency0(inputs.annualRent)}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Operating Expenses</p>
                              <p className="mt-2 text-xl font-black text-rose-300">{formatCurrency0(inputs.annualTaxes + inputs.annualInsurance + inputs.annualHoa + inputs.annualMaintenance + currentYearUtilitiesAnnual)}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Forecast Anchor</p>
                              <p className="mt-2 text-xl font-black text-emerald-300">{baseYear}</p>
                            </div>
                          </div>
                        </>
                      ) : activeHistoricalActual ? (
                        <>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <div>
                              <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Actual Year</label>
                              <input
                                type="number"
                                value={activeHistoricalActual.year}
                                max={baseYear - 1}
                                onChange={(e: any) => updateHistoricalActual(activeHistoricalActual.id, { year: Math.min(baseYear - 1, Number(e.target.value) || baseYear - 1) })}
                                className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                              />
                            </div>
                            {[
                              ['annualRent', 'Annual Rent'],
                              ['annualTaxes', 'Annual Taxes'],
                              ['annualInsurance', 'Annual Insurance'],
                              ['annualHoa', 'Annual HOA'],
                              ['annualMaintenance', 'Annual Maintenance'],
                              ['utilities', 'Utilities']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</label>
                                <input
                                  type="number"
                                  value={(activeHistoricalActual as any)[field] || 0}
                                  onChange={(e: any) => updateHistoricalActual(activeHistoricalActual.id, { [field]: Number(e.target.value) || 0 } as Partial<HistoricalActualYear>)}
                                  className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500"
                                />
                              </div>
                            ))}
                          </div>
                          <textarea
                            value={activeHistoricalActual.notes}
                            onChange={(e: any) => updateHistoricalActual(activeHistoricalActual.id, { notes: e.target.value })}
                            className="mt-4 min-h-[96px] w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-slate-200 outline-none focus:border-emerald-500"
                            placeholder="Notes about occupancy, one-time repairs, market change, or unusual performance"
                          />
                        </>
                      ) : null}
                    </div>
                  )}

                  {annualTimelineSection(true)}

                  <div className="rounded-[2rem] border border-slate-700/50 bg-slate-800 p-6 shadow-xl shadow-black/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Linked Debt</p>
                        <p className="mt-2 text-lg font-black text-white">{modelResult.debtSnapshot?.debtName || 'Unlevered'}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocation Method</p>
                        <p className="mt-2 text-lg font-black text-white">{modelResult.metrics.allocationMethod ? modelResult.metrics.allocationMethod.replace(/_/g, ' ') : 'None'}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocation %</p>
                        <p className="mt-2 text-lg font-black text-emerald-300">{formatPercent2(modelResult.metrics.allocationPercent)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocated Loan Balance</p>
                        <p className="mt-2 text-lg font-black text-white">{formatCurrency0(modelResult.metrics.allocatedCurrentLoanBalance)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Allocated Original Loan</p>
                        <p className="mt-2 text-lg font-black text-white">{formatCurrency0(modelResult.metrics.allocatedOriginalLoanAmount)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[2rem] border border-slate-700/50 bg-slate-800 p-6 shadow-xl shadow-black/30">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Monthly Projection Table</p>
                        <h3 className="mt-3 text-2xl font-black text-white">Month-by-Month Loan And Cash Flow</h3>
                        <p className="mt-2 max-w-3xl text-sm text-slate-400">Inspect the real monthly engine directly. This table shows the first {Math.max(24, chartRangeMonths)} months of balances, debt split, reserve funding, reserve draws, and cash flow without annual smoothing.</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {CHART_RANGES.map((months) => (
                          <button
                            key={months}
                            onClick={() => setChartRangeMonths(months)}
                            className={`rounded-xl border px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition-all ${chartRangeMonths === months ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' : 'border-slate-700 bg-slate-900/70 text-slate-400 hover:border-slate-500 hover:text-slate-200'}`}
                          >
                            {months}m
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full min-w-[1650px] text-left text-[11px]">
                        <thead>
                          <tr className="border-b border-slate-700 bg-slate-900 text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
                            <th className="sticky left-0 z-20 bg-slate-900 px-6 py-5">Month</th>
                            <th className="sticky left-[88px] z-20 bg-slate-900 px-6 py-5">Period</th>
                            <th className="px-6 py-5 text-right">Beg Loan Bal</th>
                            <th className="px-6 py-5 text-right">Gross Rent</th>
                            <th className="px-6 py-5 text-right">Vacancy</th>
                            <th className="px-6 py-5 text-right">Other Inc</th>
                            <th className="px-6 py-5 text-right">OpEx</th>
                            <th className="px-6 py-5 text-right text-rose-400">Interest</th>
                            <th className="px-6 py-5 text-right text-cyan-300">Principal</th>
                            <th className="px-6 py-5 text-right text-rose-400">Debt Pmt</th>
                            <th className="px-6 py-5 text-right">CF Pre Res</th>
                            <th className="px-6 py-5 text-right text-cyan-300">Repair Res</th>
                            <th className="px-6 py-5 text-right text-cyan-300">Turnover Res</th>
                            <th className="px-6 py-5 text-right text-rose-300">Reserve Out</th>
                            <th className="px-6 py-5 text-right text-emerald-400">CF After Res</th>
                            <th className="px-6 py-5 text-right">Reserve Bal</th>
                            <th className="px-6 py-5 text-right">End Loan Bal</th>
                            <th className="px-6 py-5 text-right">Cum Principal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyAnalysisRows.map((row: MonthlyProjectionRow) => {
                            const operatingExpenseTotal = row.propertyTaxes + row.insurance + row.hoa + row.propertyManagement + row.routineMaintenance + row.utilities;
                            return (
                              <tr key={row.month} className="group border-b border-slate-700/50 transition-colors hover:bg-slate-700/30">
                                <td className="sticky left-0 z-10 bg-slate-800 px-6 py-4 font-black text-slate-200 group-hover:bg-slate-700/30">Month {row.month}</td>
                                <td className="sticky left-[88px] z-10 bg-slate-800 px-6 py-4 font-mono text-slate-400 group-hover:bg-slate-700/30">{row.periodStart}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-400">{formatCurrency2(row.beginningLoanBalance)}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-300">{formatCurrency2(row.grossScheduledRent)}</td>
                                <td className="px-6 py-4 text-right font-mono text-rose-300">-{formatCurrency2(row.vacancyLoss)}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-400">{formatCurrency2(row.otherIncome + row.otherEventIncome)}</td>
                                <td className="px-6 py-4 text-right font-mono text-rose-400">-{formatCurrency2(operatingExpenseTotal)}</td>
                                <td className="px-6 py-4 text-right font-mono text-rose-300">-{formatCurrency2(row.interestPaid)}</td>
                                <td className="px-6 py-4 text-right font-mono text-cyan-300">{formatCurrency2(row.principalPaid)}</td>
                                <td className="px-6 py-4 text-right font-mono text-rose-500">-{formatCurrency2(row.totalDebtService)}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-300">{formatCurrency2(row.operatingCashFlowBeforeReserves)}</td>
                                <td className="px-6 py-4 text-right font-mono text-cyan-300">{formatCurrency2(row.repairReserve)}</td>
                                <td className="px-6 py-4 text-right font-mono text-cyan-300">{formatCurrency2(row.turnoverReserve)}</td>
                                <td className="px-6 py-4 text-right font-mono text-rose-300">-{formatCurrency2(row.reserveDraw)}</td>
                                <td className={`px-6 py-4 text-right font-black ${row.endingCashFlow >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency2(row.endingCashFlow)}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-300">{formatCurrency2(row.endingReserveBalance)}</td>
                                <td className="px-6 py-4 text-right font-mono text-slate-500">{formatCurrency2(row.endingLoanBalance)}</td>
                                <td className="px-6 py-4 text-right font-mono text-cyan-200">{formatCurrency2(row.cumulativePrincipalPaid)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-3xl border border-slate-700/50 shadow-xl shadow-black/30 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-[11px] min-w-[1350px]">
                        <thead>
                          <tr className="bg-slate-900 text-slate-400 font-black uppercase text-[9px] tracking-[0.15em] border-b border-slate-700">
                            <th className="px-6 py-5 sticky left-0 bg-slate-900 z-10">Year</th>
                            <th className="px-6 py-5 text-right">Gross Rent</th>
                            <th className="px-6 py-5 text-right">EGI</th>
                            <th className="px-6 py-5 text-right">Expenses</th>
                            <th className="px-6 py-5 text-right">NOI</th>
                            <th className="px-6 py-5 text-right text-rose-400">Interest</th>
                            <th className="px-6 py-5 text-right text-cyan-300">Principal</th>
                            <th className="px-6 py-5 text-right text-rose-400">Debt</th>
                            <th className="px-6 py-5 text-right">CF Pre Res</th>
                            <th className="px-6 py-5 text-right text-cyan-400">CapEx Res</th>
                            <th className="px-6 py-5 text-right text-emerald-400">Net CF</th>
                            <th className="px-6 py-5 text-right">Loan Bal</th>
                            <th className="px-6 py-5 text-right">Cum Principal</th>
                            <th className="px-6 py-5 text-right">Sale Price</th>
                            <th className="px-6 py-5 text-right">Net Sale Proceeds</th>
                            <th className="px-6 py-5 text-right">Reserve Release</th>
                            <th className="px-6 py-5 text-right text-emerald-400 bg-emerald-950/20">Yield %</th>
                            <th className="px-6 py-5 text-right text-cyan-400">MOIC</th>
                            <th className="px-6 py-5 text-right text-amber-400">Exit IRR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modelResult.proForma.map((row: YearData) => (
                            <tr key={row.year} className="hover:bg-slate-700/30 transition-colors border-b border-slate-700/50 group">
                              <td className="px-6 py-4 font-black sticky left-0 bg-slate-800 group-hover:bg-slate-700/30 z-10 text-slate-200">Year {row.year}</td>
                              <td className="px-6 py-4 font-mono text-right text-slate-400">{formatCurrency2(row.grossRent)}</td>
                              <td className="px-6 py-4 font-mono text-right text-slate-400">{formatCurrency2(row.egi)}</td>
                              <td className="px-6 py-4 font-mono text-right text-rose-400">-{formatCurrency2(row.expenses)}</td>
                              <td className="px-6 py-4 font-bold text-right text-slate-200">{formatCurrency2(row.noi)}</td>
                              <td className="px-6 py-4 font-mono text-right text-rose-300">-{formatCurrency2(row.interestPaid)}</td>
                              <td className="px-6 py-4 font-mono text-right text-cyan-300">{formatCurrency2(row.principalPaid)}</td>
                              <td className="px-6 py-4 font-mono text-right text-rose-500">-{formatCurrency2(row.debtService)}</td>
                              <td className="px-6 py-4 font-mono text-right text-slate-300">{formatCurrency2(row.operatingCashFlowBeforeReserves)}</td>
                              <td className="px-6 py-4 font-mono text-right text-cyan-300">-{formatCurrency2(row.capexReserve)}</td>
                              <td className="px-6 py-4 font-black text-right text-emerald-400">{formatCurrency2(row.operatingCf)}</td>
                              <td className="px-6 py-4 font-mono text-right text-slate-500">{formatCurrency2(row.loanBalance)}</td>
                              <td className="px-6 py-4 font-mono text-right text-cyan-200">{formatCurrency2(row.cumulativePrincipalPaid)}</td>
                              <td className="px-6 py-4 font-mono text-right text-slate-400">{formatCurrency0(row.assumedSalePrice)}</td>
                              <td className="px-6 py-4 font-bold text-right text-slate-200">{formatCurrency0(row.netSaleProceeds)}</td>
                              <td className="px-6 py-4 font-mono text-right text-cyan-200">{formatCurrency0(row.reserveRelease)}</td>
                              <td className="px-6 py-4 text-emerald-400 font-black text-right bg-emerald-950/20">{formatPercent2(row.coc)}</td>
                              <td className="px-6 py-4 text-cyan-400 font-bold text-right">{formatNumber2(row.moic)}x</td>
                              <td className="px-6 py-4 text-amber-400 font-black text-right">{formatPercent2(row.irr)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'risk' && (
                <RiskAnalysisView
                  modelResult={isCurrentSaved ? modelResult : null}
                  inputs={isCurrentSaved ? inputs : null}
                  riskProfile={isCurrentSaved ? riskProfile : null}
                  savedScenarios={savedScenarios}
                  auditLog={auditLog}
                  baseYear={baseYear}
                  activePropertyId={activePropertyId}
                  propertyName={propertyName}
                  properties={Object.values(properties)}
                  debts={debts}
                  debtLinks={debtLinks}
                  debtEvents={debtEvents}
                  onRiskProfileChange={setRiskProfile}
                  onSaveScenario={handleSaveScenario}
                  onDeleteScenario={handleDeleteScenario}
                  onRunScenario={handleRunScenario}
                />
              )}
            </>
          )}
        </div>
      </main>
      <AskWrennenDrawer context={askWrennenContext} />
    </div>
  );
};

export default App;
